import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../../../shared/services/supabase';
import { fetchZendeskTickets, ermittleOffeneTickets, ladeGespeicherteTicketzahlen, aktualisiereTicketzahlen, type ZendeskTicketCount, type ZendeskTicketzahl } from '../../../shared/services/zendesk';
import { Customer, CustomerMonthlyStatus, CustomerDeadlineView, StatusField, SnoozedDeadlineInfo, DatenAnlieferungInfo, LohnscheinNachzuegler, LohnscheinLaufFertigmeldungen, SpezialfristArt, SpezialfristNachzuegler, SpezialfristFertigmeldungen, CustomerIndividualDeadline, IndividualNachzuegler } from '../../../shared/types';
import { calculateEffectiveLohnlaufDate, calculateLohnscheinVersandForLauf, calculateLohnsteuerDeadline, calculateOppmQrVersandMitPeriode, calculateMonatsuebersichtQrVersandMitPeriode, getCurrentWorkingMonth, getLastCompletedMonth, type LohnscheinVersandLaufConfig } from '../../../shared/utils/dateUtils';
import { notificationService } from '../../../services/notification/notificationService';
import { calculateIsOverdue, calculateCustomerPriority, getIndividualDeadlineKey, getIndividualNachzueglerKey, hasOppmQrVersand, hasMonatsuebersichtQrVersand, istOffenNachFinalisierung, istWartetAufKunden, lohnscheinRelativAus } from '../../../shared/utils/deadlineUtils';
import { customerTodoService } from '../../../services/customer/CustomerTodoService';
import { customerAttachmentService } from '../../../services/customer/CustomerAttachmentService';
import { customerIndividualDeadlineService } from '../../../services/customer/CustomerIndividualDeadlineService';
import { EmailSenderIdentity } from '../../../services/email/types';
import { staffService } from '../../../services/staff/StaffService';
import { payrollPdfAnalyzerService } from '../../../services/payroll/payrollPdfAnalyzerService';
import { payrollDataService } from '../../../services/payroll/payrollDataService';
import { invoiceService } from '../../../services/invoice/invoiceService';
import { datevPendingTaskService } from '../../../services/datev/datevPendingTaskService';
import { ReleaseVersandSteuerung } from '../components/ReleaseModal';

// Auftrags-Status, die auf der Kachel sichtbar gemacht werden.
// 'wartet' / 'nachladen' / 'versandbereit' -> gelber Wartezustand.
// 'eskaliert'                              -> roter Zustand, nichts wurde versendet.
// Eine Konstante fuer alle Ladepfade, damit loadData und der Nachzuegler-Lader
// nicht auseinanderlaufen koennen.
const LOHNSCHEIN_AUFTRAG_SICHTBAR = ['wartet', 'nachladen', 'versandbereit', 'eskaliert'];

// Auftrags-Status des Monatsuebersichten-Stemplers, die auf der Kachel sichtbar
// sind. 'stempeln'/'zippen' -> gelber Wartezustand, 'eskaliert' -> rot.
// 'abgeschlossen' faellt heraus, dort setzt der Worker die Frist selbst.
const MONATSUEBERSICHT_AUFTRAG_SICHTBAR = ['stempeln', 'zippen', 'eskaliert'];

// Auftrags-Status des Gehaltsdokumente-Stemplers. Seit dem Umbau auf den Worker
// (11.09.2026) ist der Pending-Task nach Sekunden erledigt; die eigentliche
// Arbeit laeuft im Auftrag. Gleiche Semantik wie bei den Monatsuebersichten.
const PAYSLIPS_QR_AUFTRAG_SICHTBAR = ['stempeln', 'zippen', 'eskaliert'];

/**
 * Fertigmeldungen des Lohnschein-Versands je Lohnlauf aus einer View lesen.
 *
 * Quelle ist ausschließlich die Tabelle lohnschein_versand_lauf_status, gehalten
 * in den View-Feldern lohnscheinCompleted1/2. Die Werte werden bewusst NICHT in
 * das Monatsstatus-Objekt gespiegelt: Jeder Pfad, der den Status frisch aus der
 * Datenbank lädt, ersetzt das Objekt komplett und würde eine Spiegelung
 * verlieren. Genau daran ist eine bereits erledigte Lohnschein-Frist zuvor
 * wieder rot geworden.
 */
const laufFertigmeldungen = (view: CustomerDeadlineView): LohnscheinLaufFertigmeldungen => ({
  lauf1: view.lohnscheinCompleted1,
  lauf2: view.lohnscheinCompleted2
});

/**
 * Erledigung der beiden periodenbezogenen Spezialfristen aus einer View lesen.
 *
 * Quelle ist ausschliesslich customer_periodenfrist_status, gehalten in den
 * View-Feldern oppmQrCompleted / monatsuebersichtQrCompleted. Wie bei den
 * Lohnschein-Fertigmeldungen bewusst KEINE Spiegelung in das Monatsstatus-
 * Objekt: jeder Pfad, der den Status frisch laedt, ersetzt das Objekt komplett
 * und wuerde eine Spiegelung stillschweigend verlieren.
 */
const spezialfristFertigmeldungen = (view: CustomerDeadlineView): SpezialfristFertigmeldungen => ({
  oppmQr: view.oppmQrCompleted,
  monatsuebersichtQr: view.monatsuebersichtQrCompleted
});

// Exportierte Funktion für Wiederverwendung - nutzt erweiterte Prioritätsberechnung
export const getCustomerPriority = (view: CustomerDeadlineView): number => {
  return calculateCustomerPriority(
    view.deadlines, 
    view.status, 
    view.customer, 
    view.individualDeadlines,
    view.nachzuegler,
    laufFertigmeldungen(view),
    spezialfristFertigmeldungen(view),
    view.spezialfristNachzuegler,
    view.lohnscheinRelativ,
    view.individualNachzuegler
  );
};

/**
 * NACHZÜGLER (reine Funktion): offene Lohnschein-Läufe des zuletzt
 * abgeschlossenen Monats.
 *
 * Warum es das gibt: Die Lohnschein-Frist hängt am Arbeitsmonat. Bei relativ
 * konfigurierten Läufen ist der Inhaltsmonat gleich dem Arbeitsmonat. Sobald
 * der Monat finalisiert wird, springt die Kachel auf den Folgemonat und eine
 * noch offene Lohnschein-Frist des Vormonats wäre nicht mehr erreichbar.
 *
 * Bewusst nur EIN Monat zurück (getLastCompletedMonth). Weiter zurückliegende
 * Fälle gehören in die serverseitige Eskalation, nicht in die Oberfläche.
 *
 * Ein Lauf wird übersprungen, sobald eine Fertigmeldung in
 * lohnschein_versand_lauf_status vorliegt oder er nicht datierbar ist.
 */
const baueNachzueglerListe = (
  customer: Customer,
  statuses: CustomerMonthlyStatus[],
  configs: LohnscheinVersandLaufConfig[],
  laufStatusMap: Map<string, boolean>,
  auftragMap: Map<string, { status: string; last_error: string | null }>
): LohnscheinNachzuegler[] => {
  if (!configs || configs.length === 0) return [];

  const letzter = getLastCompletedMonth(customer, statuses);
  if (!letzter) return [];

  // Status des abgeschlossenen Monats: liefert der relativen Frist den Anker
  // (ggf. markierte Datenanlieferung), damit das Datum identisch zu dem ist,
  // das die Kachel vor der Finalisierung angezeigt hat.
  const letzterStatus = statuses.find(s =>
    s.customer_id === customer.id &&
    s.year === letzter.year &&
    s.month === letzter.month
  ) || null;

  const liste: LohnscheinNachzuegler[] = [];

  for (const cfg of configs) {
    const erledigt = laufStatusMap.get(`${customer.id}_${letzter.year}_${letzter.month}_${cfg.lohnlauf}`) || false;
    if (erledigt) continue;

    const res = calculateLohnscheinVersandForLauf(customer, cfg, letzter.year, letzter.month, letzterStatus);
    if (!res.faelligkeit) continue;

    const auftrag = auftragMap.get(
      `${customer.id}_${res.periode.year}_${res.periode.month}_${cfg.lohnlauf}`
    ) || null;

    liste.push({
      lohnlauf: cfg.lohnlauf,
      arbeitsjahr: letzter.year,
      arbeitsmonat: letzter.month,
      faelligkeit: res.faelligkeit,
      periode: res.periode,
      auftrag
    });
  }

  return liste;
};

/**
 * NACHZÜGLER SPEZIALFRISTEN (reine Funktion): noch offene QR-Fristen des zuletzt
 * abgeschlossenen Monats.
 *
 * Warum es das gibt: Seit die Kachel im Arbeitsmonat M die Periode M zeigt,
 * faellt ihre Faelligkeit mit dem Tag zusammen, an dem M finalisiert wird.
 * Springt der Arbeitsmonat auf M+1, waere eine noch offene QR-Frist ohne diese
 * Kachel nicht mehr erreichbar. Gleiche Ueberlegung wie bei den Lohnschein-
 * Nachzuegeln, deshalb auch dieselbe Beschraenkung auf genau EINEN Monat
 * zurueck (getLastCompletedMonth).
 *
 * Eine Frist wird uebersprungen, sobald fuer ihre Periode ein
 * Erledigungsvermerk vorliegt oder sie nicht datierbar ist. Ist der feste
 * Tag-Zweig konfiguriert, ist die Periode ohnehin der Vormonat; dort entsteht
 * durch den Monatswechsel keine Luecke, die Kachel bleibt aber konsistent.
 */
const baueSpezialfristNachzueglerListe = (
  customer: Customer,
  statuses: CustomerMonthlyStatus[],
  fristStatusMap: Map<string, boolean>,
  payslipsAuftragMap: Map<string, { status: string; last_error: string | null }>,
  oppmTaskMap: Map<string, { status: string; last_error: string | null }>,
  mueTaskMap: Map<string, { status: string; last_error: string | null }>
): SpezialfristNachzuegler[] => {
  const letzter = getLastCompletedMonth(customer, statuses);
  if (!letzter) return [];

  // Status des abgeschlossenen Monats: liefert dem Anker eine ggf. dort
  // markierte Datenanlieferung, damit das Datum identisch zu dem ist, das die
  // Kachel vor der Finalisierung angezeigt hat.
  const letzterStatus = statuses.find(s =>
    s.customer_id === customer.id &&
    s.year === letzter.year &&
    s.month === letzter.month
  ) || null;

  const liste: SpezialfristNachzuegler[] = [];

  if (hasOppmQrVersand(customer)) {
    const res = calculateOppmQrVersandMitPeriode(customer, letzter.year, letzter.month, letzterStatus);
    const erledigt = fristStatusMap.get(
      `${customer.id}_oppm_qr_versand_${res.periode.year}_${res.periode.month}`
    ) || false;
    if (res.faelligkeit && !erledigt) {
      liste.push({
        frist_art: 'oppm_qr_versand',
        arbeitsjahr: letzter.year,
        arbeitsmonat: letzter.month,
        faelligkeit: res.faelligkeit,
        periode: res.periode,
        auftrag: payslipsAuftragMap.get(`${customer.id}_${res.periode.year}_${res.periode.month}`)
          || oppmTaskMap.get(`${customer.id}_${res.periode.year}_${res.periode.month}`)
          || null
      });
    }
  }

  if (hasMonatsuebersichtQrVersand(customer)) {
    const res = calculateMonatsuebersichtQrVersandMitPeriode(customer, letzter.year, letzter.month, letzterStatus);
    const erledigt = fristStatusMap.get(
      `${customer.id}_monatsuebersicht_qr_versand_${res.periode.year}_${res.periode.month}`
    ) || false;
    if (res.faelligkeit && !erledigt) {
      liste.push({
        frist_art: 'monatsuebersicht_qr_versand',
        arbeitsjahr: letzter.year,
        arbeitsmonat: letzter.month,
        faelligkeit: res.faelligkeit,
        periode: res.periode,
        auftrag: mueTaskMap.get(`${customer.id}_${res.periode.year}_${res.periode.month}`) || null
      });
    }
  }

  return liste;
};

/**
 * NACHZÜGLER INDIVIDUELLE FRISTEN (reine Funktion): noch offene individuelle
 * Fristen des zuletzt abgeschlossenen Monats.
 *
 * Gilt nur für wiederkehrende Fristen mit offen_nach_finalisierung_ab, und nur
 * wenn der abgeschlossene Monat ab diesem Monat liegt (istOffenNachFinalisierung).
 * Solche Fristen hakt die Finalisierung nicht automatisch ab. Ohne diese Kachel
 * wäre eine bei der Finalisierung noch offene Frist nach dem Monatswechsel nicht
 * mehr erreichbar. Gleiche Beschränkung auf genau EINEN Monat zurück wie bei den
 * übrigen Nachzüglern (getLastCompletedMonth).
 *
 * Erledigt ist eine Frist, sobald im Monatsstatus des abgeschlossenen Monats
 * individual_deadlines_status[id] gesetzt ist. Nicht datierbare Fristen werden
 * übersprungen.
 */
const baueIndividualNachzueglerListe = (
  customer: Customer,
  statuses: CustomerMonthlyStatus[],
  individualDeadlines: CustomerIndividualDeadline[]
): IndividualNachzuegler[] => {
  if (!individualDeadlines || individualDeadlines.length === 0) return [];

  const letzter = getLastCompletedMonth(customer, statuses);
  if (!letzter) return [];

  // Status des abgeschlossenen Monats: Ziel der Erledigung und Anker für
  // relative Fristen (ggf. markierte Datenanlieferung), damit das Datum
  // identisch zu dem ist, das die Kachel vor der Finalisierung angezeigt hat.
  const letzterStatus = statuses.find(s =>
    s.customer_id === customer.id &&
    s.year === letzter.year &&
    s.month === letzter.month
  ) || null;
  if (!letzterStatus) return [];

  const liste: IndividualNachzuegler[] = [];

  for (const ind of individualDeadlines) {
    if (!istOffenNachFinalisierung(ind, letzter.year, letzter.month)) continue;
    if (letzterStatus.individual_deadlines_status?.[ind.id]) continue;

    const faelligkeit = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
      letzter.year,
      letzter.month,
      ind,
      customer,
      letzterStatus
    );
    if (!faelligkeit) continue;

    liste.push({
      deadline_id: ind.id,
      titel: ind.title,
      beschreibung: ind.description,
      arbeitsjahr: letzter.year,
      arbeitsmonat: letzter.month,
      faelligkeit,
      status_id: letzterStatus.id
    });
  }

  return liste;
};

/** Schluessel der Periodenfrist-Map: Kunde, Fristart und Abrechnungsperiode. */
const fristStatusKey = (
  customerId: string,
  fristArt: SpezialfristArt,
  jahr: number,
  monat: number
): string => `${customerId}_${fristArt}_${jahr}_${monat}`;

/**
 * ZENDESK-TICKETZAHLEN: Höchstalter des gespeicherten Stands, bevor beim Laden
 * ein neuer Abruf angestoßen wird. Die Netlify-Funktion zendesk-ticketzahlen.js
 * prüft denselben Abstand über ihre Sperre noch einmal serverseitig, damit
 * mehrere gleichzeitig geöffnete Boards nur einen Abruf auslösen.
 */
const TICKETZAHLEN_MAX_ALTER_MS = 60 * 1000;

/** ZENDESK-TICKETZAHLEN: Zendesk-Tags aller Kunden einer Ansicht, ohne Dubletten und leere Werte. */
const zendeskTagsAus = (views: CustomerDeadlineView[]): string[] =>
  Array.from(new Set(
    views
      .map(view => view.customer.zendesk_tag)
      .filter((tag): tag is string => !!tag && tag.trim() !== '')
  ));

/**
 * ZENDESK-TICKETZAHLEN: Fehlt für mindestens einen Kunden mit Zendesk-Tag die
 * Ticketzahl oder ist ihr Stand älter als TICKETZAHLEN_MAX_ALTER_MS? Dann lohnt
 * ein neuer Abruf.
 */
const ticketzahlenVeraltet = (views: CustomerDeadlineView[]): boolean => {
  const grenze = Date.now() - TICKETZAHLEN_MAX_ALTER_MS;
  return views.some(view => {
    const tag = view.customer.zendesk_tag;
    if (!tag || tag.trim() === '') return false;
    if (view.openTicketsCount === undefined || !view.openTicketsStand) return true;
    const stand = new Date(view.openTicketsStand).getTime();
    return isNaN(stand) || stand < grenze;
  });
};

/**
 * ZENDESK-TICKETZAHLEN: Gespeicherte Ticketzahlen auf die Ansicht anwenden.
 * Kunden ohne gespeicherte Anzahl bleiben unverändert.
 */
const mitGespeichertenTicketzahlen = (
  views: CustomerDeadlineView[],
  zahlen: Map<string, ZendeskTicketzahl>
): CustomerDeadlineView[] =>
  views.map(view => {
    const tag = view.customer.zendesk_tag;
    const zahl = tag ? zahlen.get(tag) : undefined;
    if (!zahl || zahl.anzahl === null) return view;
    return { ...view, openTicketsCount: zahl.anzahl, openTicketsStand: zahl.abgerufenAm };
  });

/**
 * ZENDESK-TICKETZAHLEN: Live abgefragte Ticketzahlen auf die Ansicht anwenden.
 * Unbekannte Werte (null) bleiben außen vor: Das Badge bleibt dann
 * ausgeblendet, statt eine falsche 0 zu zeigen.
 */
const mitLiveTicketzahlen = (
  views: CustomerDeadlineView[],
  zahlen: ZendeskTicketCount,
  stand: string
): CustomerDeadlineView[] =>
  views.map(view => {
    const tag = view.customer.zendesk_tag;
    const anzahl = tag ? zahlen[tag] : undefined;
    if (typeof anzahl !== 'number') return view;
    return { ...view, openTicketsCount: anzahl, openTicketsStand: stand };
  });

export const useDeadlines = () => {
  const [customerDeadlineViews, setCustomerDeadlineViews] = useState<CustomerDeadlineView[]>([]);
  // Zwei-Lauf-Modell: geladene Lohnschein-Konfigurationen pro Kunde, damit die
  // Rebuild-Pfade (Finalisierung, Monats-Reset) die Kacheln pro Lauf neu bauen.
  const lohnscheinConfigsRef = useRef<Map<string, LohnscheinVersandLaufConfig[]>>(new Map());
  // ZENDESK-TICKETZAHLEN: verhindert, dass dasselbe Board mehrere Abrufe
  // gleichzeitig anstößt (etwa bei schnellen Tab-Wechseln).
  const ticketzahlenAbrufLaeuftRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [currentFinalizationData, setCurrentFinalizationData] = useState<{
    view: CustomerDeadlineView;
    field: StatusField;
  } | null>(null);
  const [showZendeskWarning, setShowZendeskWarning] = useState(false);
  const [zendeskWarningData, setZendeskWarningData] = useState<{
    view: CustomerDeadlineView;
    field: StatusField;
    // null = Zendesk konnte nicht geprüft werden (Abfrage fehlgeschlagen)
    ticketCount: number | null;
  } | null>(null);

  // Lohnschein-Versand (Stufe 3.2, Baustein C): Modal-State für den Klick-Versand.
  // arbeitsjahr/arbeitsmonat sind der Monat, ZU DEM die Frist gehört. Bei einer
  // regulären Kachel ist das der Arbeitsmonat des Kunden, bei einem Nachzügler
  // der zuletzt abgeschlossene Monat. Das Modal und die Fertigmeldung brauchen
  // diesen Monat, nicht den aktuellen Arbeitsmonat.
  const [showLohnscheinModal, setShowLohnscheinModal] = useState(false);
  const [lohnscheinVersandData, setLohnscheinVersandData] = useState<{
    view: CustomerDeadlineView;
    field: StatusField;
    lohnlauf: 1 | 2;
    versandJahr: number;
    versandMonat: number;
    arbeitsjahr: number;
    arbeitsmonat: number;
    istNachzuegler: boolean;
  } | null>(null);

  // OPPM QR-Code-Versand: Modal-State fuer den Klick-Versand (Release-artig).
  // nachzuegler ist gesetzt, wenn der Klick von einer Nachzuegler-Kachel kam;
  // dann gelten dessen Periode und Arbeitsmonat statt der der laufenden Kachel.
  const [showOppmQrModal, setShowOppmQrModal] = useState(false);
  const [oppmQrVersandData, setOppmQrVersandData] = useState<{
    view: CustomerDeadlineView;
    field: StatusField;
    nachzuegler?: SpezialfristNachzuegler;
  } | null>(null);

  // OPPM Monatsuebersicht QR-Code-Versand: Modal-State (Release-artig)
  const [showMonatsuebersichtQrModal, setShowMonatsuebersichtQrModal] = useState(false);
  const [monatsuebersichtQrVersandData, setMonatsuebersichtQrVersandData] = useState<{
    view: CustomerDeadlineView;
    field: StatusField;
    nachzuegler?: SpezialfristNachzuegler;
  } | null>(null);
  
  // NEUE STATE: Loading für spezifische Buttons
  const [buttonLoadingStates, setButtonLoadingStates] = useState<{
    [customerIdAndField: string]: boolean;
  }>({});

  // NEUE HELPER-FUNKTIONEN für Button Loading States
  const setButtonLoading = (customerId: string, field: StatusField, loading: boolean) => {
    const key = `${customerId}_${field}`;
    setButtonLoadingStates(prev => ({
      ...prev,
      [key]: loading
    }));
  };

  const isButtonLoading = (customerId: string, field: StatusField): boolean => {
    const key = `${customerId}_${field}`;
    return buttonLoadingStates[key] || false;
  };

  /**
   * NACHZÜGLER (Lader für Einzelkunden): baut die Liste in den Rebuild-Pfaden
   * (nach Finalisierung, nach Monats-Reset), wo die globalen Maps aus loadData
   * nicht zur Verfügung stehen. Zwei kleine, auf den Kunden gefilterte Abfragen.
   * Die Logik selbst liegt in baueNachzueglerListe, damit sie nur einmal existiert.
   */
  const ladeNachzueglerFuerKunden = async (
    customer: Customer,
    statuses: CustomerMonthlyStatus[]
  ): Promise<LohnscheinNachzuegler[]> => {
    const configs = lohnscheinConfigsRef.current.get(customer.id) || [];
    if (configs.length === 0) return [];

    const letzter = getLastCompletedMonth(customer, statuses);
    if (!letzter) return [];

    const laufStatusMap = new Map<string, boolean>();
    const { data: laufStatusRows } = await supabase
      .from('lohnschein_versand_lauf_status')
      .select('lohnlauf, completed')
      .eq('customer_id', customer.id)
      .eq('arbeitsjahr', letzter.year)
      .eq('arbeitsmonat', letzter.month);
    (laufStatusRows || []).forEach((s: { lohnlauf: number; completed: boolean }) => {
      laufStatusMap.set(`${customer.id}_${letzter.year}_${letzter.month}_${s.lohnlauf}`, !!s.completed);
    });

    const auftragMap = new Map<string, { status: string; last_error: string | null }>();
    const { data: auftragRows } = await supabase
      .from('lohnschein_versand_auftraege')
      .select('versand_jahr, versand_monat, lohnlauf, status, last_error')
      .eq('customer_id', customer.id)
      .in('status', LOHNSCHEIN_AUFTRAG_SICHTBAR)
      .order('created_at', { ascending: true });
    (auftragRows || []).forEach((a: { versand_jahr: number; versand_monat: number; lohnlauf: number; status: string; last_error: string | null }) => {
      auftragMap.set(`${customer.id}_${a.versand_jahr}_${a.versand_monat}_${a.lohnlauf}`, { status: a.status, last_error: a.last_error });
    });

    return baueNachzueglerListe(customer, statuses, configs, laufStatusMap, auftragMap);
  };

  /**
   * SPEZIALFRISTEN (Lader fuer Einzelkunden): ermittelt Erledigung, offene
   * Auftraege und Nachzuegler in den Rebuild-Pfaden (nach Finalisierung, nach
   * Monats-Reset), wo die globalen Maps aus loadData nicht zur Verfuegung
   * stehen. Auf den Kunden gefilterte Abfragen; die Nachzuegler-Logik selbst
   * liegt in baueSpezialfristNachzueglerListe, damit sie nur einmal existiert.
   */
  const ladeSpezialfristStandFuerKunden = async (
    customer: Customer,
    statuses: CustomerMonthlyStatus[],
    oppmPeriode?: { year: number; month: number },
    muePeriode?: { year: number; month: number }
  ): Promise<{
    oppmQrCompleted: boolean;
    monatsuebersichtQrCompleted: boolean;
    oppmQrVersandTask: { status: string; last_error: string | null } | null;
    monatsuebersichtQrVersandTask: { status: string; last_error: string | null } | null;
    nachzuegler: SpezialfristNachzuegler[];
  }> => {
    const leer = {
      oppmQrCompleted: false,
      monatsuebersichtQrCompleted: false,
      oppmQrVersandTask: null,
      monatsuebersichtQrVersandTask: null,
      nachzuegler: [] as SpezialfristNachzuegler[]
    };

    if (!hasOppmQrVersand(customer) && !hasMonatsuebersichtQrVersand(customer)) {
      return leer;
    }

    const fristStatusMap = new Map<string, boolean>();
    const { data: fristRows } = await supabase
      .from('customer_periodenfrist_status')
      .select('frist_art, periode_jahr, periode_monat, completed')
      .eq('customer_id', customer.id)
      .limit(100000);
    (fristRows || []).forEach((f: { frist_art: string; periode_jahr: number; periode_monat: number; completed: boolean }) => {
      fristStatusMap.set(
        fristStatusKey(customer.id, f.frist_art as SpezialfristArt, f.periode_jahr, f.periode_monat),
        !!f.completed
      );
    });

    const oppmTaskMap = new Map<string, { status: string; last_error: string | null }>();
    const { data: oppmRows } = await supabase
      .from('datev_pending_tasks')
      .select('year, month, status, last_error')
      .eq('customer_id', customer.id)
      .eq('task_type', 'OPPM_payslips_qr_code')
      .in('status', ['pending', 'partially_fulfilled'])
      .limit(100000);
    (oppmRows || []).forEach((t: { year: number; month: number; status: string; last_error: string | null }) => {
      oppmTaskMap.set(`${customer.id}_${t.year}_${t.month}`, { status: t.status, last_error: t.last_error });
    });

    // Stempel-Auftraege des Gehaltsdokumente-Workers. Vorrang vor dem
    // Pending-Task, weil dort die eigentliche Arbeit laeuft.
    const payslipsAuftragMap = new Map<string, { status: string; last_error: string | null }>();
    const { data: payslipsRows } = await supabase
      .from('payslips_qr_versand_auftraege')
      .select('jahr, monat, status, last_error')
      .eq('customer_id', customer.id)
      .in('status', PAYSLIPS_QR_AUFTRAG_SICHTBAR)
      .order('created_at', { ascending: true })
      .limit(100000);
    (payslipsRows || []).forEach((a: { jahr: number; monat: number; status: string; last_error: string | null }) => {
      payslipsAuftragMap.set(`${customer.id}_${a.jahr}_${a.monat}`, { status: a.status, last_error: a.last_error });
    });

    const mueTaskMap = new Map<string, { status: string; last_error: string | null }>();
    const { data: mueRows } = await supabase
      .from('monatsuebersicht_versand_auftraege')
      .select('jahr, monat, status, last_error')
      .eq('customer_id', customer.id)
      .in('status', MONATSUEBERSICHT_AUFTRAG_SICHTBAR)
      .order('created_at', { ascending: true })
      .limit(100000);
    (mueRows || []).forEach((a: { jahr: number; monat: number; status: string; last_error: string | null }) => {
      mueTaskMap.set(`${customer.id}_${a.jahr}_${a.monat}`, { status: a.status, last_error: a.last_error });
    });

    return {
      oppmQrCompleted: oppmPeriode
        ? (fristStatusMap.get(fristStatusKey(customer.id, 'oppm_qr_versand', oppmPeriode.year, oppmPeriode.month)) || false)
        : false,
      monatsuebersichtQrCompleted: muePeriode
        ? (fristStatusMap.get(fristStatusKey(customer.id, 'monatsuebersicht_qr_versand', muePeriode.year, muePeriode.month)) || false)
        : false,
      oppmQrVersandTask: oppmPeriode
        ? (payslipsAuftragMap.get(`${customer.id}_${oppmPeriode.year}_${oppmPeriode.month}`)
           || oppmTaskMap.get(`${customer.id}_${oppmPeriode.year}_${oppmPeriode.month}`)
           || null)
        : null,
      monatsuebersichtQrVersandTask: muePeriode
        ? (mueTaskMap.get(`${customer.id}_${muePeriode.year}_${muePeriode.month}`) || null)
        : null,
      nachzuegler: baueSpezialfristNachzueglerListe(customer, statuses, fristStatusMap, payslipsAuftragMap, oppmTaskMap, mueTaskMap)
    };
  };

  // SNOOZE FEATURE: Hilfsfunktion um aktuellen User zu ermitteln
  // Stufe 3: gibt zusätzlich die E-Mail zurück (Innenidentität für den
  // terminierten Versand); bestehende Aufrufer (Snooze) ignorieren das Feld.
  const getCurrentUserInfo = async (): Promise<{ name: string; initials: string; email: string } | null> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;
      
      // Base64 URL zu Standard Base64 konvertieren (gleiche Logik wie in useAuth.ts)
      const base64UrlDecode = (str: string) => {
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (base64.length % 4) {
          case 2: base64 += '=='; break;
          case 3: base64 += '='; break;
        }
        return atob(base64);
      };
      
      const payloadBase64 = token.split('.')[1];
      const payloadJson = base64UrlDecode(payloadBase64);
      const payload = JSON.parse(payloadJson);
      const email = payload.email;
      
      if (!email) return null;
      
      // Versuche den Mitarbeiter aus xtax_staff zu laden (für korrekte Initialen)
      const { data: staffData } = await supabase
        .from('xtax_staff')
        .select('full_name, initials')
        .eq('email', email)
        .eq('is_active', true)
        .single();
      
      if (staffData) {
        return { name: staffData.full_name, initials: staffData.initials, email };
      }
      
      // Fallback: Name aus Token, Initialen generieren
      const name = payload.name || 'Unbekannt';
      const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      return { name, initials, email };
    } catch {
      return null;
    }
  };

  // Hilfsfunktion: aktuelle Staff-UUID ermitteln (für finalized_by Tracking)
  // Gibt die UUID des angemeldeten Mitarbeiters aus xtax_staff zurück, oder null falls nicht gefunden
  const getCurrentStaffId = async (): Promise<string | null> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;

      const base64UrlDecode = (str: string) => {
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (base64.length % 4) {
          case 2: base64 += '=='; break;
          case 3: base64 += '='; break;
        }
        return atob(base64);
      };

      const payloadBase64 = token.split('.')[1];
      const payloadJson = base64UrlDecode(payloadBase64);
      const payload = JSON.parse(payloadJson);
      const email = payload.email;

      if (!email) return null;

      const { data: staffData } = await supabase
        .from('xtax_staff')
        .select('id')
        .eq('email', email)
        .eq('is_active', true)
        .single();

      return staffData?.id || null;
    } catch {
      return null;
    }
  };

  // ABSENDER BEIM KLICK (30.10.2026): Ist beim Versand noch kein Absender
  // gewählt, weil die Absenderliste nach dem Öffnen des Fensters noch lädt,
  // wird die angemeldete Person als Absender eingesetzt. Quelle ist dieselbe
  // Abfrage wie für finalized_by, damit Absender und "finalisiert von" nicht
  // auseinanderlaufen. Ein bewusst gewählter Absender hat immer Vorrang.
  // Gelingt die Ermittlung nicht, bleibt es beim bisherigen Verhalten.
  const ermittleAbsender = async (
    senderIdentity?: EmailSenderIdentity
  ): Promise<EmailSenderIdentity | undefined> => {
    if (senderIdentity) return senderIdentity;

    const staffId = await getCurrentStaffId();
    if (!staffId) {
      console.warn('[Absender] Kein Absender gewählt und angemeldete Person nicht ermittelbar, bisheriges Verhalten greift');
      return undefined;
    }

    const absender = await staffService.getStaffById(staffId);
    if (!absender) {
      console.warn('[Absender] Kein Absender gewählt und Mitarbeiter nicht ladbar, bisheriges Verhalten greift');
      return undefined;
    }

    console.log(`[Absender] Kein Absender gewählt, angemeldete Person wird eingesetzt: ${absender.fullName}`);
    return absender;
  };

  /**
   * ZENDESK-TICKETZAHLEN: Aktualisiert die Ticket-Badges im Hintergrund.
   *
   * Die Zahlen kommen mit loadData aus dem Zwischenspeicher
   * (zendesk_ticketzahlen) und sind sofort sichtbar. Fehlt der Stand für einen
   * Kunden oder ist er älter als eine Minute, stößt diese Funktion den Abruf
   * bei Zendesk an und liest den Zwischenspeicher danach neu. Ob tatsächlich
   * abgerufen wird, entscheidet die Netlify-Funktion über ihre Sperre; läuft
   * gerade ein Abruf eines anderen Boards, wird nur der gespeicherte Stand
   * gelesen. Für Kunden, für die danach noch keine Zahl vorliegt (etwa direkt
   * nach der Neuanlage), wird wie bisher live nachgefragt. Wirft nie.
   */
  const aktualisiereTicketzahlenImHintergrund = useCallback(async (views: CustomerDeadlineView[]) => {
    const tags = zendeskTagsAus(views);
    if (tags.length === 0 || !ticketzahlenVeraltet(views)) return;
    if (ticketzahlenAbrufLaeuftRef.current) return;
    ticketzahlenAbrufLaeuftRef.current = true;

    try {
      await aktualisiereTicketzahlen();

      const zahlen = await ladeGespeicherteTicketzahlen();
      if (zahlen) {
        setCustomerDeadlineViews(prevViews => mitGespeichertenTicketzahlen(prevViews, zahlen));
      }

      const fehlend = tags.filter(tag => {
        const zahl = zahlen ? zahlen.get(tag) : undefined;
        return !zahl || zahl.anzahl === null;
      });
      if (fehlend.length > 0) {
        const liveZahlen = await fetchZendeskTickets(fehlend);
        const stand = new Date().toISOString();
        setCustomerDeadlineViews(prevViews => mitLiveTicketzahlen(prevViews, liveZahlen, stand));
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Zendesk-Ticketzahlen:', error);
    } finally {
      ticketzahlenAbrufLaeuftRef.current = false;
    }
  }, []);

  // Erweiterte loadData Funktion mit Individual Deadlines
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // PERFORMANCE: Alle Abfragen, die nicht voneinander abhängen, laufen in
      // zwei Wellen parallel statt nacheinander. Inhalt der Abfragen und
      // Verarbeitung sind unverändert; berechnet wird erst, wenn alle Antworten
      // vorliegen.
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();

      // Welle 1: alle Abfragen ohne Abhängigkeit von der Kundenliste
      const [
        { data: customersData, error: customersError },
        { data: deadlinesData, error: deadlinesError },
        { data: statusData, error: statusError },
        { data: lohnscheinAuftraege },
        { data: lohnscheinConfigs },
        { data: lohnscheinLaufStatus },
        { data: oppmQrTasks },
        { data: monatsuebersichtAuftraege },
        { data: payslipsAuftraege },
        { data: periodenfristStatus },
        gespeicherteTicketzahlen
      ] = await Promise.all([
        // Load core data from Supabase
        supabase
          .from('customers')
          .select(`
            *,
            zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
              id,
              full_name,
              initials
            )
          `)
          .eq('deleted', false) // Filter für nicht-gelöschte Kunden
          .eq('ist_testmandant', false) // Testmandanten erscheinen nicht im Fristenboard
          .eq('ist_testmandant', false) // Testmandanten erscheinen nicht im Fristenboard
          .order('company'),

        supabase
          .from('monthly_deadlines')
          .select('*')
          .gte('year', currentYear - 1)
          .order('year', { ascending: true })
          .order('month', { ascending: true }),

        supabase
          .from('customer_monthly_status')
          .select('*')
          .limit(100000),

        // Punkt 4 (Lohnschein-Versand asynchron): offene Versand-Aufträge laden
        // (Status wartet/nachladen/versandbereit) und nach customer + Versandperiode
        // indizieren. Steuert den gelben "beauftragt/wartend"-Zustand der Kachel.
        // Zusätzlich werden eskalierte Aufträge geladen: dort wurde NICHTS versendet,
        // die Kachel muss das rot zeigen statt stillschweigend offen zu bleiben.
        // Sortierung aufsteigend nach created_at, damit bei einem wieder
        // aufgenommenen Auftrag der neuere den älteren eskalierten überschreibt.
        supabase
          .from('lohnschein_versand_auftraege')
          .select('customer_id, versand_jahr, versand_monat, lohnlauf, status, last_error')
          .in('status', LOHNSCHEIN_AUFTRAG_SICHTBAR)
          .order('created_at', { ascending: true })
          .limit(100000),

        // Zwei-Lauf-Modell: aktive Lohnschein-Konfigurationen pro Kunde/Lohnlauf.
        supabase
          .from('customer_lohnschein_versand_configs')
          .select('customer_id, lohnlauf, tag, verschiebung, monat_offset, bezug_werktage_offset')
          .eq('is_active', true)
          .limit(100000),

        // Zwei-Lauf-Modell: Fertigmeldungen pro Kunde/Arbeitsmonat/Lohnlauf.
        supabase
          .from('lohnschein_versand_lauf_status')
          .select('customer_id, arbeitsjahr, arbeitsmonat, lohnlauf, completed')
          .limit(100000),

        // OPPM QR-Code-Versand: offene Pending-Tasks (pending/partially_fulfilled)
        // laden und nach customer + Versandperiode (task.year/month = Vormonat)
        // indizieren. Steuert den gelben "beauftragt/wartend"-Zustand der Kachel.
        supabase
          .from('datev_pending_tasks')
          .select('customer_id, year, month, status, last_error')
          .eq('task_type', 'OPPM_payslips_qr_code')
          .in('status', ['pending', 'partially_fulfilled'])
          .limit(100000),

        // OPPM Monatsuebersicht QR-Code-Versand: Wartezustand kommt seit dem
        // ausgelagerten Stempler NICHT mehr aus dem Pending-Task. Der Task ist
        // nach Sekunden erledigt, die eigentliche Arbeit laeuft im Auftrag.
        // 'stempeln'/'zippen' -> gelb, 'eskaliert' -> rot (nichts versendet).
        supabase
          .from('monatsuebersicht_versand_auftraege')
          .select('customer_id, jahr, monat, status, last_error')
          .in('status', MONATSUEBERSICHT_AUFTRAG_SICHTBAR)
          .order('created_at', { ascending: true })
          .limit(100000),

        // OPPM QR-Code-Versand: offene bzw. eskalierte Stempel-Auftraege. Der
        // Wartezustand kommt seit dem Worker-Umbau NICHT mehr allein aus dem
        // Pending-Task: der ist nach Sekunden erledigt, waehrend das Stempeln
        // ueber mehrere Minuten laeuft.
        supabase
          .from('payslips_qr_versand_auftraege')
          .select('customer_id, jahr, monat, status, last_error')
          .in('status', PAYSLIPS_QR_AUFTRAG_SICHTBAR)
          .order('created_at', { ascending: true })
          .limit(100000),

        // Periodenbezogene Spezialfristen: Erledigungsvermerke je Kunde, Fristart
        // und ABRECHNUNGSPERIODE. Loest die frueheren Booleans am Monatsstatus ab,
        // die nur eine Periode je Arbeitsmonat tragen konnten.
        supabase
          .from('customer_periodenfrist_status')
          .select('customer_id, frist_art, periode_jahr, periode_monat, completed')
          .limit(100000),

        // ZENDESK-TICKETZAHLEN: zuletzt gespeicherte Ticketzahlen je Zendesk-Tag.
        // Damit erscheinen die Badges sofort mit den Kundendaten. Ist der Stand
        // veraltet, frischt aktualisiereTicketzahlenImHintergrund ihn nach dem
        // Laden auf. Liefert null, wenn die Tabelle nicht lesbar ist; das Laden
        // der Kundendaten wird davon nicht beeinträchtigt.
        ladeGespeicherteTicketzahlen()
      ]);

      // Fehlerbehandlung wie bisher: ohne Kunden, Fristtabelle oder Monatsstatus
      // wird abgebrochen, in derselben Reihenfolge wie zuvor.
      if (customersError) {
        console.error('Error loading customers:', customersError);
        return;
      }

      if (deadlinesError) {
        console.error('Error loading deadlines:', deadlinesError);
        return;
      }

      if (statusError) {
        console.error('Error loading status:', statusError);
        return;
      }

      // Welle 2: Abfragen, die die Kundenliste brauchen.
      // To-Dos, Attachments und Individual Deadlines für alle Kunden sowie die
      // Auftraggeber. Auftraggeber werden separat geladen (Self-Reference über
      // PostgREST-Embed ist bei customers → customers unzuverlässig).
      const customerIds = (customersData || []).map(customer => customer.id);
      const auftraggeberIds = Array.from(new Set(
        (customersData || [])
          .map(c => c.auftraggeber_id)
          .filter((id): id is string => !!id)
      ));
      const [customerTodos, customerAttachments, customerIndividualDeadlines, auftraggeberResult] = await Promise.all([
        customerTodoService.getTodosForCustomers(customerIds),
        customerAttachmentService.getAttachmentsForCustomers(customerIds),
        customerIndividualDeadlineService.getIndividualDeadlinesForCustomers(customerIds),
        auftraggeberIds.length > 0
          ? supabase
              .from('customers')
              .select('id, company')
              .in('id', auftraggeberIds)
          : null
      ]);

      const lohnscheinAuftragMap = new Map<string, { status: string; last_error: string | null }>();
      (lohnscheinAuftraege || []).forEach((a: { customer_id: string; versand_jahr: number; versand_monat: number; lohnlauf: number; status: string; last_error: string | null }) => {
        lohnscheinAuftragMap.set(`${a.customer_id}_${a.versand_jahr}_${a.versand_monat}_${a.lohnlauf}`, { status: a.status, last_error: a.last_error });
      });

      const lohnscheinConfigMap = new Map<string, LohnscheinVersandLaufConfig[]>();
      (lohnscheinConfigs || []).forEach((c: { customer_id: string; lohnlauf: number; tag: number | null; verschiebung: string | null; monat_offset: number | null; bezug_werktage_offset: number | null }) => {
        const list = lohnscheinConfigMap.get(c.customer_id) || [];
        list.push({ lohnlauf: c.lohnlauf as 1 | 2, tag: c.tag, verschiebung: c.verschiebung, monat_offset: c.monat_offset, bezug_werktage_offset: c.bezug_werktage_offset });
        lohnscheinConfigMap.set(c.customer_id, list);
      });
      lohnscheinConfigsRef.current = lohnscheinConfigMap;

      const lohnscheinLaufStatusMap = new Map<string, boolean>();
      (lohnscheinLaufStatus || []).forEach((s: { customer_id: string; arbeitsjahr: number; arbeitsmonat: number; lohnlauf: number; completed: boolean }) => {
        lohnscheinLaufStatusMap.set(`${s.customer_id}_${s.arbeitsjahr}_${s.arbeitsmonat}_${s.lohnlauf}`, !!s.completed);
      });

      const oppmQrTaskMap = new Map<string, { status: string; last_error: string | null }>();
      (oppmQrTasks || []).forEach((t: { customer_id: string; year: number; month: number; status: string; last_error: string | null }) => {
        oppmQrTaskMap.set(`${t.customer_id}_${t.year}_${t.month}`, { status: t.status, last_error: t.last_error });
      });

      const monatsuebersichtQrTaskMap = new Map<string, { status: string; last_error: string | null }>();
      (monatsuebersichtAuftraege || []).forEach((a: { customer_id: string; jahr: number; monat: number; status: string; last_error: string | null }) => {
        monatsuebersichtQrTaskMap.set(`${a.customer_id}_${a.jahr}_${a.monat}`, { status: a.status, last_error: a.last_error });
      });

      const payslipsAuftragMap = new Map<string, { status: string; last_error: string | null }>();
      (payslipsAuftraege || []).forEach((a: { customer_id: string; jahr: number; monat: number; status: string; last_error: string | null }) => {
        payslipsAuftragMap.set(`${a.customer_id}_${a.jahr}_${a.monat}`, { status: a.status, last_error: a.last_error });
      });

      const fristStatusMap = new Map<string, boolean>();
      (periodenfristStatus || []).forEach((f: { customer_id: string; frist_art: string; periode_jahr: number; periode_monat: number; completed: boolean }) => {
        fristStatusMap.set(
          fristStatusKey(f.customer_id, f.frist_art as SpezialfristArt, f.periode_jahr, f.periode_monat),
          !!f.completed
        );
      });

      const auftraggeberMap = new Map<string, { id: string; company: string }>();
      if (auftraggeberResult) {
        const { data: auftraggeberData, error: auftraggeberError } = auftraggeberResult;
        if (auftraggeberError) {
          console.error('Error loading auftraggeber:', auftraggeberError);
        } else {
          (auftraggeberData || []).forEach(ag => {
            auftraggeberMap.set(ag.id, { id: ag.id, company: ag.company });
          });
        }
      }
      (customersData || []).forEach(customer => {
        customer.auftraggeber = customer.auftraggeber_id
          ? auftraggeberMap.get(customer.auftraggeber_id) || null
          : null;
      });

      // Create customer deadline views WITH Individual Deadlines
      const views: CustomerDeadlineView[] = (customersData || []).map(customer => {
        const workingMonth = getCurrentWorkingMonth(customer, statusData || []);

        // Find current status
        // WICHTIG (Frist ab Anlieferung): Der Status-Lookup steht VOR der
        // Fristberechnung, weil die SSoT-Funktion calculateEffectiveLohnlaufDate
        // die Anlieferungsmarkierung aus dem Status braucht. Verhaltensneutral
        // für alle anderen Fristen.
        const status = (statusData || []).find(s => 
          s.customer_id === customer.id && 
          s.year === workingMonth.year && 
          s.month === workingMonth.month
        );

        // Hinweis: Der frueher hier gebildete Vormonats-Status entfaellt. Die
        // relativen Spezialfristen ankern seit der Perioden-Korrektur am
        // Lohnlauf des ARBEITSMONATS und bekommen deshalb den Monatsstatus des
        // Arbeitsmonats (status) als Ankerstatus uebergeben.

        // Calculate standard deadlines
        const deadlines: CustomerDeadlineView['deadlines'] = {};
        
        if (customer.erster_lohnlauf_tag) {
          deadlines.lohnlauf_1 = calculateEffectiveLohnlaufDate(
            customer,
            1,
            workingMonth.year,
            workingMonth.month,
            status || null
          );
        }
        
        if (customer.zweiter_lohnlauf_tag) {
          deadlines.lohnlauf_2 = calculateEffectiveLohnlaufDate(
            customer,
            2,
            workingMonth.year,
            workingMonth.month,
            status || null
          );
        }

        // Lohnschein-Versand an Arbeitnehmer (Stufe 3.2): Frist im Arbeitsmonat
        // für die Vormonats-Periode via SSoT (calculateLohnscheinVersandDate).
        // Fester Tag oder Lohnlauf-Bezug.
        // Zwei-Lauf-Modell: eine Kachel je konfiguriertem Lohnlauf. Faelligkeit
        // und Inhaltsmonat kommen aus calculateLohnscheinVersandForLauf.
        const lsConfigs = lohnscheinConfigMap.get(customer.id) || [];
        let lohnscheinVersandPeriode1: { year: number; month: number } | undefined;
        let lohnscheinVersandPeriode2: { year: number; month: number } | undefined;
        for (const cfg of lsConfigs) {
          const res = calculateLohnscheinVersandForLauf(customer, cfg, workingMonth.year, workingMonth.month, status || null);
          if (cfg.lohnlauf === 1) {
            deadlines.lohnschein_versand_1 = res.faelligkeit;
            lohnscheinVersandPeriode1 = res.periode;
          } else {
            deadlines.lohnschein_versand_2 = res.faelligkeit;
            lohnscheinVersandPeriode2 = res.periode;
          }
        }

        // OPPM QR-Code-Versand: Faelligkeit UND Abrechnungsperiode via SSoT.
        // Ankerstatus ist der Monatsstatus des ARBEITSMONATS, weil der Anker im
        // relativen Zweig der Lohnlauf des Arbeitsmonats ist.
        let oppmQrPeriode: { year: number; month: number } | undefined;
        if (hasOppmQrVersand(customer)) {
          const res = calculateOppmQrVersandMitPeriode(
            customer,
            workingMonth.year,
            workingMonth.month,
            status || null
          );
          deadlines.oppm_qr_versand = res.faelligkeit;
          oppmQrPeriode = res.periode;
        }

        // OPPM Monatsuebersicht QR-Code-Versand: analog.
        let monatsuebersichtQrPeriode: { year: number; month: number } | undefined;
        if (hasMonatsuebersichtQrVersand(customer)) {
          const res = calculateMonatsuebersichtQrVersandMitPeriode(
            customer,
            workingMonth.year,
            workingMonth.month,
            status || null
          );
          deadlines.monatsuebersicht_qr_versand = res.faelligkeit;
          monatsuebersichtQrPeriode = res.periode;
        }
        
        // GEÄNDERT: Verwende neue calculateLohnsteuerDeadline Funktion
        const lohnsteuerDeadline = calculateLohnsteuerDeadline(
          workingMonth.year,
          workingMonth.month,
          customer.lohnsteuer_zeitraum,
          deadlinesData || []
        );
        if (lohnsteuerDeadline) {
          deadlines.lohnsteuer = lohnsteuerDeadline;
        }
        
        // Standard SV-Beiträge Logik bleibt unverändert
        const deadline = (deadlinesData || []).find(d => d.year === workingMonth.year && d.month === workingMonth.month);
        if (deadline?.beitragsnachweis_frist) {
          deadlines.sv_beitraege = new Date(deadline.beitragsnachweis_frist);
        }

        // Calculate individual deadlines
        const individualDeadlines = customerIndividualDeadlines[customer.id] || [];
        individualDeadlines.forEach(individualDeadline => {
          const deadlineKey = getIndividualDeadlineKey(individualDeadline.id);
          deadlines[deadlineKey] = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
            workingMonth.year,
            workingMonth.month,
            individualDeadline,
            customer,
            status || null
          );
        });

        // Get To-Dos and Attachments for this customer
        const customTodos = customerTodos[customer.id] || [];
        const customAttachments = customerAttachments[customer.id] || [];

        // Punkt 4 (Lohnschein-Versand asynchron): offener Versand-Auftrag für
        // die Versandperiode (Vormonat des Arbeitsmonats) an die View hängen,
        // damit die Kachel den gelben "beauftragt/wartend"-Zustand zeigen kann.
        // Zwei-Lauf-Modell: offener Auftrag je Lauf (Schluessel inkl. Lohnlauf,
        // Versandperiode = pro Lauf berechneter Inhaltsmonat).
        const lohnscheinVersandAuftrag1 = lohnscheinVersandPeriode1
          ? (lohnscheinAuftragMap.get(`${customer.id}_${lohnscheinVersandPeriode1.year}_${lohnscheinVersandPeriode1.month}_1`) || null)
          : null;
        const lohnscheinVersandAuftrag2 = lohnscheinVersandPeriode2
          ? (lohnscheinAuftragMap.get(`${customer.id}_${lohnscheinVersandPeriode2.year}_${lohnscheinVersandPeriode2.month}_2`) || null)
          : null;
        // Fertigmeldung je Lauf aus der Lauf-Statustabelle. Sie wandert als
        // eigenes View-Feld an die Kachel (lohnscheinCompleted1/2) und wird
        // bewusst NICHT in das Status-Objekt gespiegelt.
        const lohnscheinCompleted1 = lohnscheinLaufStatusMap.get(`${customer.id}_${workingMonth.year}_${workingMonth.month}_1`) || false;
        const lohnscheinCompleted2 = lohnscheinLaufStatusMap.get(`${customer.id}_${workingMonth.year}_${workingMonth.month}_2`) || false;
        // WARTET AUF KUNDEN: relativ konfigurierte Lohnschein-Läufe ruhen mit
        // ihrem Lohnlauf, solange dieser beim Kunden liegt.
        const lohnscheinRelativ = lohnscheinRelativAus(lsConfigs);

        // NACHZÜGLER: noch offene Lohnschein-Läufe des zuletzt abgeschlossenen
        // Monats. Ohne sie verschwindet eine bei der Finalisierung noch offene
        // Lohnschein-Frist mit dem Monatswechsel aus der Oberfläche.
        const nachzuegler = baueNachzueglerListe(
          customer,
          statusData || [],
          lsConfigs,
          lohnscheinLaufStatusMap,
          lohnscheinAuftragMap
        );

        // NACHZÜGLER SPEZIALFRISTEN: noch offene QR-Fristen des zuletzt
        // abgeschlossenen Monats. Gegenstueck zu den Lohnschein-Nachzuegeln.
        const spezialfristNachzuegler = baueSpezialfristNachzueglerListe(
          customer,
          statusData || [],
          fristStatusMap,
          payslipsAuftragMap,
          oppmQrTaskMap,
          monatsuebersichtQrTaskMap
        );

        // NACHZÜGLER INDIVIDUELLE FRISTEN: noch offene individuelle Fristen des
        // zuletzt abgeschlossenen Monats (nur Fristen mit offen_nach_finalisierung_ab).
        const individualNachzuegler = baueIndividualNachzueglerListe(
          customer,
          statusData || [],
          individualDeadlines
        );

        // OPPM QR-Code-Versand: Wartezustand fuer die berechnete Abrechnungsperiode.
        // Der Auftrag hat Vorrang (dort laeuft die eigentliche Arbeit), der
        // Pending-Task deckt das Fenster davor ab, in dem noch auf die DATEV-
        // Dokumente gewartet wird. Ohne beide Quellen waere die Kachel in einer
        // der beiden Phasen faelschlich unbeauftragt.
        const oppmQrVersandTask = oppmQrPeriode
          ? (payslipsAuftragMap.get(`${customer.id}_${oppmQrPeriode.year}_${oppmQrPeriode.month}`)
             || oppmQrTaskMap.get(`${customer.id}_${oppmQrPeriode.year}_${oppmQrPeriode.month}`)
             || null)
          : null;

        // OPPM Monatsuebersicht QR-Code-Versand: analog.
        const monatsuebersichtQrVersandTask = monatsuebersichtQrPeriode
          ? (monatsuebersichtQrTaskMap.get(`${customer.id}_${monatsuebersichtQrPeriode.year}_${monatsuebersichtQrPeriode.month}`) || null)
          : null;

        // Erledigung je Frist fuer genau diese Periode. Eigene View-Felder statt
        // Spiegelung in das Statusobjekt (siehe spezialfristFertigmeldungen).
        const oppmQrCompleted = oppmQrPeriode
          ? (fristStatusMap.get(fristStatusKey(customer.id, 'oppm_qr_versand', oppmQrPeriode.year, oppmQrPeriode.month)) || false)
          : false;
        const monatsuebersichtQrCompleted = monatsuebersichtQrPeriode
          ? (fristStatusMap.get(fristStatusKey(customer.id, 'monatsuebersicht_qr_versand', monatsuebersichtQrPeriode.year, monatsuebersichtQrPeriode.month)) || false)
          : false;

        // ZENDESK-TICKETZAHLEN: gespeicherte Anzahl samt Stand. Ohne
        // gespeicherte Anzahl bleibt das Feld leer, das Badge ist dann
        // ausgeblendet, bis der Hintergrundabruf oder die Live-Abfrage greift.
        const ticketzahl = customer.zendesk_tag && gespeicherteTicketzahlen
          ? gespeicherteTicketzahlen.get(customer.zendesk_tag)
          : undefined;
        const ticketzahlVorhanden = !!ticketzahl && ticketzahl.anzahl !== null;

        return {
          customer,
          currentMonth: workingMonth.month,
          currentYear: workingMonth.year,
          deadlines,
          status: status || null,
          isOverdue: calculateIsOverdue(deadlines, status || null, customer, individualDeadlines, nachzuegler, { lauf1: lohnscheinCompleted1, lauf2: lohnscheinCompleted2 }, { oppmQr: oppmQrCompleted, monatsuebersichtQr: monatsuebersichtQrCompleted }, spezialfristNachzuegler, lohnscheinRelativ, individualNachzuegler),
          openTicketsCount: ticketzahlVorhanden ? (ticketzahl!.anzahl as number) : undefined,
          openTicketsStand: ticketzahlVorhanden ? ticketzahl!.abgerufenAm : undefined,
          customTodos,
          customAttachments,
          individualDeadlines, // Neue Property
          lohnscheinVersandAuftrag1,
          lohnscheinVersandAuftrag2,
          lohnscheinVersandPeriode1,
          lohnscheinVersandPeriode2,
          lohnscheinCompleted1,
          lohnscheinCompleted2,
          lohnscheinRelativ,
          nachzuegler,
          oppmQrVersandTask,
          monatsuebersichtQrVersandTask,
          oppmQrPeriode,
          monatsuebersichtQrPeriode,
          oppmQrCompleted,
          monatsuebersichtQrCompleted,
          spezialfristNachzuegler,
          individualNachzuegler
        };
      });

      // Sortiere nach Priorität (dringendste zuerst) - nutzt erweiterte Prioritätsberechnung
      const sortedViews = views.sort((a, b) => {
        const priorityA = calculateCustomerPriority(a.deadlines, a.status, a.customer, a.individualDeadlines, a.nachzuegler, laufFertigmeldungen(a), spezialfristFertigmeldungen(a), a.spezialfristNachzuegler, a.lohnscheinRelativ, a.individualNachzuegler);
        const priorityB = calculateCustomerPriority(b.deadlines, b.status, b.customer, b.individualDeadlines, b.nachzuegler, laufFertigmeldungen(b), spezialfristFertigmeldungen(b), b.spezialfristNachzuegler, b.lohnscheinRelativ, b.individualNachzuegler);
        
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        
        return a.customer.company.localeCompare(b.customer.company);
      });

      setCustomerDeadlineViews(sortedViews);

      // ZENDESK-TICKETZAHLEN: veralteten oder fehlenden Stand im Hintergrund
      // auffrischen. Bewusst ohne await, damit das Board nicht auf Zendesk wartet.
      aktualisiereTicketzahlenImHintergrund(sortedViews);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [aktualisiereTicketzahlenImHintergrund]);

  // Window Focus Event für automatische Updates beim Tab-Wechsel
  useEffect(() => {
    let lastUpdateTime = Date.now();
    const MIN_UPDATE_INTERVAL = 60000; // 60 Sekunden zwischen Updates
    
    const handleFocusUpdate = () => {
      const now = Date.now();
      
      // Verhindere zu häufige Updates
      if (now - lastUpdateTime < MIN_UPDATE_INTERVAL) {
        console.log('⏳ Zu schneller Focus-Wechsel (< 60 Sek), überspringe Update');
        return;
      }
      
      // Nicht updaten wenn bereits am Laden oder Upload-Modal offen
      if (loading || showUploadModal || showReleaseModal || currentFinalizationData) {
        console.log('⏸️ Update übersprungen - App ist beschäftigt');
        return;
      }

      // Prüfe ob ein Modal offen ist
      // Alle Modals in der App nutzen einen Backdrop mit rgba(0, 0, 0, 0.5)
      const hasModalBackdrop = Array.from(document.querySelectorAll('div')).some(element => {
        const style = window.getComputedStyle(element);
        // Prüfe auf den typischen Modal-Backdrop
        return (
          style.position === 'fixed' &&
          style.top === '0px' &&
          style.left === '0px' &&
          style.right === '0px' &&
          style.bottom === '0px' &&
          style.backgroundColor === 'rgba(0, 0, 0, 0.5)' // Spezifischer Check für Modal-Backdrop
        );
      });
      
      if (hasModalBackdrop) {
        console.log('📝 Modal-Backdrop erkannt (Customer/Edit Modal offen) - kein Auto-Refresh');
        return;
      }
      
      console.log('👁️ Tab wieder aktiv - aktualisiere Daten...');
      lastUpdateTime = now;
      
      // Daten neu laden
      loadData();
    };
    
    // Visibility Change API (moderner, zuverlässiger)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocusUpdate();
      }
    };
    
    // Event Listeners registrieren
    window.addEventListener('focus', handleFocusUpdate);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('focus', handleFocusUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loading, showUploadModal, showReleaseModal, currentFinalizationData, loadData]);

  // Initial load beim Component Mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  const isFinalizationStep = (view: CustomerDeadlineView, field: StatusField): boolean => {
    // Individuelle Deadlines sind niemals Finalisierungs-Schritte
    if (field.startsWith('individual_deadline_')) {
      return false;
    }
    
    // Debug-Logs
    console.log('🔍 isFinalizationStep called:', {
      field,
      customer: view.customer.company,
      zweiter_lohnlauf_tag: view.customer.zweiter_lohnlauf_tag,
      hasSecondLohnlauf: !!view.customer.zweiter_lohnlauf_tag
    });
    
    // Prüfe, ob dies ein Finalisierungs-Klick ist (nur für Standard-Deadlines)
    const hasSecondLohnlauf = !!view.customer.zweiter_lohnlauf_tag;
    
    const result = hasSecondLohnlauf 
      ? field === 'lohnlauf_2_completed'
      : field === 'lohnlauf_1_completed';
      
    console.log('🎯 isFinalizationStep result:', result);
    
    return result;
  };

  // Erweiterte toggleStatus Funktion mit Support für individuelle Deadlines UND Loading States
  const toggleStatus = async (view: CustomerDeadlineView, field: StatusField) => {
  // NEUE LOGIK: Setze Button Loading State
  setButtonLoading(view.customer.id, field, true);
  
  try {
    // Handle individual deadlines
    if (field.startsWith('individual_deadline_')) {
      const deadlineId = field.replace('individual_deadline_', '');
      
      // WICHTIG: Für einmalige Deadlines - direktes Update in der Datenbank
      const deadline = view.individualDeadlines?.find(d => d.id === deadlineId);
      if (deadline && !deadline.is_recurring) {
        // Einmalige Deadline - update direkt in customer_individual_deadlines Tabelle
        const newCompletedStatus = !deadline.completed;
        
        const { error } = await supabase
          .from('customer_individual_deadlines')
          .update({
            completed: newCompletedStatus,
            completed_at: newCompletedStatus ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', deadlineId);

        if (error) {
          console.error('Error updating one-time deadline:', error);
          alert('Fehler beim Aktualisieren der einmaligen Deadline');
          return;
        }

        // KORRIGIERT: Nur lokalen State aktualisieren statt loadData()
        setCustomerDeadlineViews(prevViews => 
          prevViews.map(prevView => {
            if (prevView.customer.id === view.customer.id) {
              // Update die spezifische Deadline im lokalen State
              const updatedDeadlines = prevView.individualDeadlines?.map(d => {
                if (d.id === deadlineId) {
                  return { 
                    ...d, 
                    completed: newCompletedStatus, 
                    completed_at: newCompletedStatus ? new Date().toISOString() : undefined 
                  };
                }
                return d;
              });
              
              return {
                ...prevView,
                individualDeadlines: updatedDeadlines,
                isOverdue: calculateIsOverdue(
                  prevView.deadlines, 
                  prevView.status, 
                  prevView.customer, 
                  updatedDeadlines,
                  prevView.nachzuegler,
                  laufFertigmeldungen(prevView),
                  spezialfristFertigmeldungen(prevView),
                  prevView.spezialfristNachzuegler,
                  prevView.lohnscheinRelativ,
                  prevView.individualNachzuegler
                )
              };
            }
            return prevView;
          })
        );
        
        return; // Früher Ausstieg für einmalige Deadlines
      }
      
      // Für wiederkehrende Deadlines: Bisherige Logik
      const currentIndividualStatus = view.status?.individual_deadlines_status?.[deadlineId] || false;
      const newValue = !currentIndividualStatus;
      
      try {
        let updatedStatus: CustomerMonthlyStatus;
        
        if (view.status) {
          // Update existing status
          const updatedIndividualStatus = {
            ...view.status.individual_deadlines_status,
            [deadlineId]: newValue
          };
          
          const { data, error } = await supabase
            .from('customer_monthly_status')
            .update({
              individual_deadlines_status: updatedIndividualStatus,
              updated_at: new Date().toISOString()
            })
            .eq('id', view.status.id)
            .select();

          if (error) throw error;
          updatedStatus = data[0];
        } else {
          // Create new status
          const newStatus: Omit<CustomerMonthlyStatus, 'id'> = {
            customer_id: view.customer.id,
            year: view.currentYear,
            month: view.currentMonth,
            lohnlauf_1_completed: false,
            lohnlauf_2_completed: false,
            lohnsteuer_completed: false,
            sv_beitraege_completed: false,
            fully_completed: false,
            individual_deadlines_status: { [deadlineId]: newValue }
          };

          const { data, error } = await supabase
            .from('customer_monthly_status')
            .insert([newStatus])
            .select();

          if (error) throw error;
          updatedStatus = data[0];
        }

        // Update local state
        setCustomerDeadlineViews(prevViews => 
          prevViews.map(prevView => {
            if (prevView.customer.id === view.customer.id) {
              return {
                ...prevView,
                status: updatedStatus,
                isOverdue: calculateIsOverdue(
                  prevView.deadlines, 
                  updatedStatus, 
                  prevView.customer, 
                  prevView.individualDeadlines,
                  prevView.nachzuegler,
                  laufFertigmeldungen(prevView),
                  spezialfristFertigmeldungen(prevView),
                  prevView.spezialfristNachzuegler,
                  prevView.lohnscheinRelativ,
                  prevView.individualNachzuegler
                )
              };
            }
            return prevView;
          })
        );

      } catch (error) {
        console.error('Error updating individual deadline status:', error);
        alert('Fehler beim Aktualisieren des Status');
      }
      return;
    }

    // Handle standard deadlines (existing logic)
    const standardField = field as keyof Omit<CustomerMonthlyStatus, 'id' | 'customer_id' | 'year' | 'month' | 'fully_completed' | 'individual_deadlines_status'>;
    const currentStatus = view.status;
    const newValue = currentStatus ? !currentStatus[standardField] : true;
    const isFinalization = isFinalizationStep(view, field);

    // Lohnschein-Versand (Stufe 3.2, Baustein C): Das Erledigen öffnet das
    // Versand-Modal (Vorschau + Versand via Edge Function lohnschein-versand);
    // die Frist wird erst nach erfolgreichem Versand über den bestehenden
    // Statuspfad erledigt. Das Zurücknehmen bleibt ein normaler Toggle.
    // Der neue Wert leitet sich aus der Lauf-Fertigmeldung an der View ab,
    // NICHT aus dem Monatsstatus — dort gibt es dieses Feld nicht.
    if (field === 'lohnschein_versand_1_completed' || field === 'lohnschein_versand_2_completed') {
      const lohnlauf: 1 | 2 = field === 'lohnschein_versand_1_completed' ? 1 : 2;
      const bereitsErledigt = lohnlauf === 1 ? !!view.lohnscheinCompleted1 : !!view.lohnscheinCompleted2;
      const neuerWert = !bereitsErledigt;
      const periode = lohnlauf === 1 ? view.lohnscheinVersandPeriode1 : view.lohnscheinVersandPeriode2;
      if (neuerWert) {
        // Erledigen oeffnet das Versand-Modal fuer diesen Lauf mit Periode.
        // Arbeitsmonat ist hier der aktuelle Arbeitsmonat des Kunden.
        if (!periode) return;
        setLohnscheinVersandData({
          view,
          field,
          lohnlauf,
          versandJahr: periode.year,
          versandMonat: periode.month,
          arbeitsjahr: view.currentYear,
          arbeitsmonat: view.currentMonth,
          istNachzuegler: false
        });
        setShowLohnscheinModal(true);
      } else {
        // Zuruecknehmen: Fertigmeldung dieses Laufs in der Lauf-Statustabelle loeschen.
        await setLohnscheinLaufCompleted(view, lohnlauf, false);
      }
      return;
    }

    // OPPM QR-Code-Versand: Das Erledigen oeffnet das Release-artige Modal
    // (Empfaenger-, Absender- und Nachrichtenwahl, dann Pending-Task-Anlage);
    // die Frist wird erst nach erfolgreichem Versand vom Edge-Handler erledigt.
    // Das Zuruecknehmen loescht den Erledigungsvermerk der Periode.
    // Der neue Wert leitet sich aus dem View-Feld ab, NICHT aus dem Monatsstatus:
    // die Erledigung haengt seit der Perioden-Korrektur an
    // customer_periodenfrist_status.
    if (field === 'oppm_qr_versand_completed') {
      if (!view.oppmQrCompleted) {
        setOppmQrVersandData({ view, field });
        setShowOppmQrModal(true);
      } else {
        await setSpezialfristCompleted(view, 'oppm_qr_versand', false);
      }
      return;
    }

    // OPPM Monatsuebersicht QR-Code-Versand: analog.
    if (field === 'monatsuebersicht_qr_versand_completed') {
      if (!view.monatsuebersichtQrCompleted) {
        setMonatsuebersichtQrVersandData({ view, field });
        setShowMonatsuebersichtQrModal(true);
      } else {
        await setSpezialfristCompleted(view, 'monatsuebersicht_qr_versand', false);
      }
      return;
    }

    // Bei Finalisierung: Zendesk-Check durchführen. Bewusst eine Live-Abfrage
    // statt des Zwischenspeichers, weil es hier auf den genauen Stand ankommt.
    // Scheitert die Abfrage, ist das Ergebnis null ("unbekannt"): Dann zeigt
    // das Warnfenster einen Hinweis, statt die Finalisierung stillschweigend
    // durchzulassen.
    if (isFinalization && newValue && view.customer.zendesk_tag) {
      let ticketCount: number | null;
      try {
        ticketCount = await ermittleOffeneTickets(view.customer.zendesk_tag);
      } catch (error) {
        console.error('Zendesk-Check fehlgeschlagen:', error);
        ticketCount = null;
      }

      if (ticketCount === null || ticketCount > 0) {
        // Zeige Zendesk-Warnung
        setZendeskWarningData({ view, field, ticketCount });
        setShowZendeskWarning(true);
        return; // Stoppe hier - Benutzer muss entscheiden
      }
    }

    // Normaler Ablauf: Finalisierung fortsetzen
    await proceedWithFinalization(view, field, newValue, isFinalization);
  } finally {
    // WICHTIG: Button Loading State immer zurücksetzen
    setButtonLoading(view.customer.id, field, false);
  }
};

  /**
   * NACHZÜGLER: Klick auf eine Nachzügler-Kachel.
   *
   * Eigener Einstieg statt toggleStatus, damit StatusField-Semantik,
   * Überfälligkeits- und Prioritätsberechnung des laufenden Monats unberührt
   * bleiben. Das Modal bekommt den Monat des Nachzüglers als Arbeitsmonat und
   * dessen Inhaltsmonat als Versandperiode.
   */
  const openLohnscheinNachzuegler = (view: CustomerDeadlineView, nachzuegler: LohnscheinNachzuegler) => {
    const field: StatusField = nachzuegler.lohnlauf === 1
      ? 'lohnschein_versand_1_completed'
      : 'lohnschein_versand_2_completed';

    setLohnscheinVersandData({
      view,
      field,
      lohnlauf: nachzuegler.lohnlauf,
      versandJahr: nachzuegler.periode.year,
      versandMonat: nachzuegler.periode.month,
      arbeitsjahr: nachzuegler.arbeitsjahr,
      arbeitsmonat: nachzuegler.arbeitsmonat,
      istNachzuegler: true
    });
    setShowLohnscheinModal(true);
  };

  const proceedWithFinalization = async (
    view: CustomerDeadlineView, 
    field: StatusField, 
    newValue: boolean, 
    isFinalization: boolean
  ) => {
    // Bei Finalisierung: Modal öffnen — xtax_portal nutzt das Release-Modal,
    // alle anderen Versandarten (email, duo, kein_versand) das Upload-Modal.
    if (isFinalization && newValue) {
      setCurrentFinalizationData({ view, field });
      if (view.customer.versandmethode === 'xtax_portal') {
        setShowReleaseModal(true);
      } else {
        setShowUploadModal(true);
      }
      return;
    }

    // Normale Status-Änderung ohne Upload
    await updateStatusInDatabase(view, field, newValue, false);
  };

  const updateStatusInDatabase = async (
    view: CustomerDeadlineView, 
    field: StatusField, 
    newValue: boolean, 
    isFinalization: boolean
  ) => {
    try {
      // Bei Finalisierung: aktuelle Staff-UUID für finalized_by Tracking ermitteln
      let currentStaffId: string | null = (isFinalization && newValue) ? await getCurrentStaffId() : null;

      // Fallback: Schlägt der Staff-Lookup bei einer Finalisierung fehl, wird statt
      // stillschweigendem NULL der zuständige Mitarbeiter des Kunden eingetragen.
      // So bleibt die Mitarbeiter-Performance-Zurechnung lückenlos.
      if (isFinalization && newValue && !currentStaffId) {
        console.warn('[Finalisierung] Staff-Lookup fehlgeschlagen — Fallback auf zuständigen Mitarbeiter des Kunden');
        currentStaffId = view.customer.zustaendiger_mitarbeiter_id || null;
      }

      // ABSICHERUNG (Doppel-INSERT): Ist der Frontend-Status leer, in der
      // Datenbank existiert aber bereits eine Zeile für den Monat, wird diese als
      // currentStatus verwendet. Dadurch läuft der Code in den UPDATE-Pfad statt
      // in einen zweiten INSERT, den die Unique-Regel (customer_id, year, month)
      // abweisen würde. Der UPDATE-Pfad merged auf die echten Bestandswerte, es
      // gehen also keine bereits gesetzten Felder verloren.
      let currentStatus = view.status;
      if (!currentStatus) {
        const { data: existingRow } = await supabase
          .from('customer_monthly_status')
          .select('*')
          .eq('customer_id', view.customer.id)
          .eq('year', view.currentYear)
          .eq('month', view.currentMonth)
          .maybeSingle();
        if (existingRow) {
          currentStatus = existingRow as CustomerMonthlyStatus;
        }
      }

      let updatedStatus: CustomerMonthlyStatus;

      if (currentStatus) {
        // Update existing status
        const updates: Partial<CustomerMonthlyStatus> = { [field]: newValue };
        
        // Bei Finalisierung: Auto-complete andere Felder
        if (isFinalization && newValue) {
          updates.lohnsteuer_completed = true;
          
          // SV-Beiträge nur wenn sv_pflicht = true
          if (view.customer.sv_pflicht) {
            updates.sv_beitraege_completed = true;
          }
          
          // WICHTIG: Nur wiederkehrende individuelle Deadlines als erledigt markieren
          // Einmalige Deadlines behalten ihren Status monatsübergreifend
          // OFFEN NACH FINALISIERUNG: Wiederkehrende Fristen mit
          // offen_nach_finalisierung_ab werden ab diesem Monat ebenfalls NICHT
          // abgehakt. Sind sie noch offen, erscheinen sie nach dem Monatswechsel
          // als Nachzügler (siehe baueIndividualNachzueglerListe).
          if (view.individualDeadlines && view.individualDeadlines.length > 0) {
            const updatedIndividualStatus = { ...(currentStatus.individual_deadlines_status || {}) };
            
            view.individualDeadlines.forEach(deadline => {
              // NUR wiederkehrende Deadlines ohne "offen nach Finalisierung"
              // werden automatisch auf erledigt gesetzt
              if (deadline.is_recurring && !istOffenNachFinalisierung(deadline, view.currentYear, view.currentMonth)) {
                updatedIndividualStatus[deadline.id] = true;
              }
              // Einmalige Deadlines (is_recurring = false) behalten ihren Status
              // Sie werden NICHT automatisch completed
            });
            
            updates.individual_deadlines_status = updatedIndividualStatus;
          }
          
          // Kunde ist komplett fertig -> wandert in nächsten Monat
          updates.fully_completed = true;
          // NEU: finalisierenden Mitarbeiter tracken (für Mitarbeiter-Performance-Auswertung)
          updates.finalized_by = currentStaffId;
        } else {
          // Normale Logik für nicht-Finalisierungs-Clicks
          const allCompleted = 
            (field === 'lohnlauf_1_completed' ? newValue : currentStatus.lohnlauf_1_completed) &&
            (field === 'lohnlauf_2_completed' ? newValue : currentStatus.lohnlauf_2_completed || !view.customer.zweiter_lohnlauf_tag) &&
            (field === 'lohnsteuer_completed' ? newValue : currentStatus.lohnsteuer_completed) &&
            (field === 'sv_beitraege_completed' ? newValue : currentStatus.sv_beitraege_completed || !view.customer.sv_pflicht);
          
          updates.fully_completed = allCompleted;
        }
        
        const { error } = await supabase
          .from('customer_monthly_status')
          .update(updates)
          .eq('id', currentStatus.id);

        if (error) throw error;

        updatedStatus = { ...currentStatus, ...updates };
      } else {
        // Create new status
        const newStatus = {
          customer_id: view.customer.id,
          year: view.currentYear,
          month: view.currentMonth,
          lohnlauf_1_completed: field === 'lohnlauf_1_completed' ? newValue : false,
          lohnlauf_2_completed: field === 'lohnlauf_2_completed' ? newValue : false,
          lohnsteuer_completed: field === 'lohnsteuer_completed' ? newValue : (isFinalization && newValue),
          sv_beitraege_completed: field === 'sv_beitraege_completed' ? newValue : (isFinalization && newValue && view.customer.sv_pflicht),
          // Lohnschein-Versand (Stufe 3.2): wird nur durch direkten Klick gesetzt,
          // niemals durch Finalisierungs-Auto-Complete
          lohnschein_versand_completed: field === 'lohnschein_versand_completed' ? newValue : false,
          // OPPM-QR-Fristen: Die Erledigung liegt seit der Perioden-Korrektur in
          // customer_periodenfrist_status, nicht mehr hier. Die beiden Spalten
          // bleiben vorerst bestehen und werden mit ihrem Default angelegt; die
          // QR-Zweige in toggleStatus erreichen diesen Pfad nicht mehr.
          oppm_qr_versand_completed: false,
          monatsuebersicht_qr_versand_completed: false,
          fully_completed: isFinalization && newValue,
          // NEU: finalisierenden Mitarbeiter tracken (für Mitarbeiter-Performance-Auswertung)
          finalized_by: currentStaffId,
        };

        const { data, error } = await supabase
          .from('customer_monthly_status')
          .insert([newStatus])
          .select();

        if (error) throw error;
        updatedStatus = data[0];
      }

      // Bei Finalisierung: Kunde wechselt in nächsten Monat -> alles neu berechnen
      if (isFinalization && newValue && updatedStatus.fully_completed) {
        // Lade alle Status-Daten neu für diesen Kunden
        const { data: allStatusData } = await supabase
          .from('customer_monthly_status')
          .select('*')
          .eq('customer_id', view.customer.id);

        if (allStatusData) {
          // Berechne den neuen Arbeitsmonat
          const newWorkingMonth = getCurrentWorkingMonth(view.customer, allStatusData);

          // Finde den Status für den neuen Monat (normalerweise null)
          // WICHTIG (Frist ab Anlieferung): Lookup VOR der Fristberechnung,
          // weil die SSoT-Funktion die Anlieferungsmarkierung aus dem Status braucht.
          const newStatus = allStatusData.find(s => 
            s.year === newWorkingMonth.year && 
            s.month === newWorkingMonth.month
          );

          // Hinweis: Der frueher hier gebildete Vormonats-Status entfaellt. Die
          // relativen Spezialfristen ankern am Lohnlauf des ARBEITSMONATS und
          // bekommen deshalb newStatus als Ankerstatus.
          
          // Lade Deadline-Daten für den neuen Monat UND FOLGEMONAT (wichtig für "monatlich_versetzt"!)
          const { data: deadlineData } = await supabase
            .from('monthly_deadlines')
            .select('*')
            .or(`and(year.eq.${newWorkingMonth.year},month.eq.${newWorkingMonth.month}),and(year.eq.${newWorkingMonth.month === 12 ? newWorkingMonth.year + 1 : newWorkingMonth.year},month.eq.${newWorkingMonth.month === 12 ? 1 : newWorkingMonth.month + 1})`);
          
          // Berechne neue Standard-Deadlines
          const newDeadlines: CustomerDeadlineView['deadlines'] = {};
          
          if (view.customer.erster_lohnlauf_tag) {
            newDeadlines.lohnlauf_1 = calculateEffectiveLohnlaufDate(
              view.customer,
              1,
              newWorkingMonth.year,
              newWorkingMonth.month,
              newStatus || null
            );
          }
          
          if (view.customer.zweiter_lohnlauf_tag) {
            newDeadlines.lohnlauf_2 = calculateEffectiveLohnlaufDate(
              view.customer,
              2,
              newWorkingMonth.year,
              newWorkingMonth.month,
              newStatus || null
            );
          }

          // Zwei-Lauf-Modell: eine Kachel je konfiguriertem Lohnlauf (neuer Arbeitsmonat).
          const lsConfigsNeu = lohnscheinConfigsRef.current.get(view.customer.id) || [];
          let newLohnscheinPeriode1: { year: number; month: number } | undefined;
          let newLohnscheinPeriode2: { year: number; month: number } | undefined;
          for (const cfg of lsConfigsNeu) {
            const res = calculateLohnscheinVersandForLauf(view.customer, cfg, newWorkingMonth.year, newWorkingMonth.month, newStatus || null);
            if (cfg.lohnlauf === 1) {
              newDeadlines.lohnschein_versand_1 = res.faelligkeit;
              newLohnscheinPeriode1 = res.periode;
            } else {
              newDeadlines.lohnschein_versand_2 = res.faelligkeit;
              newLohnscheinPeriode2 = res.periode;
            }
          }

          // NACHZÜGLER: Der soeben finalisierte Monat ist ab jetzt der zuletzt
          // abgeschlossene. War dort ein Lohnschein-Lauf noch offen, bleibt er
          // über diese Kachel erreichbar statt mit dem Monatswechsel zu verschwinden.
          const newNachzuegler = await ladeNachzueglerFuerKunden(view.customer, allStatusData);
          // WARTET AUF KUNDEN: relativ konfigurierte Lohnschein-Läufe (Konfiguration unverändert)
          const newLohnscheinRelativ = lohnscheinRelativAus(lsConfigsNeu);

          // OPPM QR-Code-Versand: Faelligkeit und Periode fuer den neuen Arbeitsmonat
          let newOppmQrPeriode: { year: number; month: number } | undefined;
          if (hasOppmQrVersand(view.customer)) {
            const res = calculateOppmQrVersandMitPeriode(
              view.customer,
              newWorkingMonth.year,
              newWorkingMonth.month,
              newStatus || null
            );
            newDeadlines.oppm_qr_versand = res.faelligkeit;
            newOppmQrPeriode = res.periode;
          }

          // OPPM Monatsuebersicht QR-Code-Versand: analog
          let newMonatsuebersichtQrPeriode: { year: number; month: number } | undefined;
          if (hasMonatsuebersichtQrVersand(view.customer)) {
            const res = calculateMonatsuebersichtQrVersandMitPeriode(
              view.customer,
              newWorkingMonth.year,
              newWorkingMonth.month,
              newStatus || null
            );
            newDeadlines.monatsuebersicht_qr_versand = res.faelligkeit;
            newMonatsuebersichtQrPeriode = res.periode;
          }

          // Erledigung und Nachzuegler der Spezialfristen frisch bestimmen.
          // Der soeben finalisierte Monat ist ab jetzt der zuletzt abgeschlossene;
          // eine dort noch offene QR-Frist bleibt ueber die Nachzuegler-Kachel
          // erreichbar statt mit dem Monatswechsel zu verschwinden.
          const newSpezialfrist = await ladeSpezialfristStandFuerKunden(
            view.customer,
            allStatusData,
            newOppmQrPeriode,
            newMonatsuebersichtQrPeriode
          );
          
          // GEÄNDERT: Verwende neue calculateLohnsteuerDeadline Funktion
          const lohnsteuerDeadline = calculateLohnsteuerDeadline(
            newWorkingMonth.year,
            newWorkingMonth.month,
            view.customer.lohnsteuer_zeitraum,
            deadlineData || []
          );
          if (lohnsteuerDeadline) {
            newDeadlines.lohnsteuer = lohnsteuerDeadline;
          }
          
          // Standard SV-Beiträge Logik bleibt unverändert
          const deadline = deadlineData?.find(d => d.year === newWorkingMonth.year && d.month === newWorkingMonth.month);
          if (deadline?.beitragsnachweis_frist) {
            newDeadlines.sv_beitraege = new Date(deadline.beitragsnachweis_frist);
          }

          // Lade individuelle Deadlines frisch aus der Datenbank
          const individualDeadlines = await customerIndividualDeadlineService.getCustomerIndividualDeadlines(view.customer.id);
          
          // Berechne individuelle Deadlines für den neuen Monat
          individualDeadlines.forEach(individualDeadline => {
            const deadlineKey = getIndividualDeadlineKey(individualDeadline.id);
            newDeadlines[deadlineKey] = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
              newWorkingMonth.year,
              newWorkingMonth.month,
              individualDeadline,
              view.customer,
              newStatus || null
            );
          });

          // NACHZÜGLER INDIVIDUELLE FRISTEN: Der soeben finalisierte Monat ist ab
          // jetzt der zuletzt abgeschlossene. Dort noch offene Fristen mit
          // offen_nach_finalisierung_ab bleiben über diese Kachel erreichbar.
          const newIndividualNachzuegler = baueIndividualNachzueglerListe(
            view.customer,
            allStatusData,
            individualDeadlines
          );
          
          // Update die komplette View für diesen Kunden mit dem neuen Monat.
          // Die Lauf-Fertigmeldungen gehören zum Arbeitsmonat und starten im
          // neuen Monat unerledigt (Verhalten wie bisher).
          setCustomerDeadlineViews(prevViews => 
            prevViews.map(prevView => {
              if (prevView.customer.id === view.customer.id) {
                return {
                  ...prevView,
                  currentMonth: newWorkingMonth.month,
                  currentYear: newWorkingMonth.year,
                  deadlines: newDeadlines,
                  status: newStatus || null,
                  isOverdue: calculateIsOverdue(newDeadlines, newStatus || null, view.customer, individualDeadlines, newNachzuegler, { lauf1: false, lauf2: false }, { oppmQr: newSpezialfrist.oppmQrCompleted, monatsuebersichtQr: newSpezialfrist.monatsuebersichtQrCompleted }, newSpezialfrist.nachzuegler, newLohnscheinRelativ, newIndividualNachzuegler),
                  individualDeadlines: individualDeadlines, // WICHTIG: individualDeadlines beibehalten!
                  lohnscheinVersandPeriode1: newLohnscheinPeriode1,
                  lohnscheinVersandPeriode2: newLohnscheinPeriode2,
                  lohnscheinVersandAuftrag1: null,
                  lohnscheinVersandAuftrag2: null,
                  lohnscheinCompleted1: false,
                  lohnscheinCompleted2: false,
                  lohnscheinRelativ: newLohnscheinRelativ,
                  nachzuegler: newNachzuegler,
                  oppmQrPeriode: newOppmQrPeriode,
                  monatsuebersichtQrPeriode: newMonatsuebersichtQrPeriode,
                  oppmQrCompleted: newSpezialfrist.oppmQrCompleted,
                  monatsuebersichtQrCompleted: newSpezialfrist.monatsuebersichtQrCompleted,
                  oppmQrVersandTask: newSpezialfrist.oppmQrVersandTask,
                  monatsuebersichtQrVersandTask: newSpezialfrist.monatsuebersichtQrVersandTask,
                  spezialfristNachzuegler: newSpezialfrist.nachzuegler,
                  individualNachzuegler: newIndividualNachzuegler
                };
              }
              return prevView;
            })
          );

          console.log(`Kunde ${view.customer.company} finalisiert und auf ${newWorkingMonth.month}/${newWorkingMonth.year} gesetzt`);
        }
      } else {
        // Normale Status-Updates: nur lokalen State aktualisieren
        setCustomerDeadlineViews(prevViews => 
          prevViews.map(prevView => {
            if (prevView.customer.id === view.customer.id) {
              return {
                ...prevView,
                status: updatedStatus,
                isOverdue: calculateIsOverdue(
                  prevView.deadlines, 
                  updatedStatus, 
                  prevView.customer, 
                  prevView.individualDeadlines,
                  prevView.nachzuegler,
                  laufFertigmeldungen(prevView),
                  spezialfristFertigmeldungen(prevView),
                  prevView.spezialfristNachzuegler,
                  prevView.lohnscheinRelativ,
                  prevView.individualNachzuegler
                )
              };
            }
            return prevView;
          })
        );
      }

    } catch (error) {
      console.error('Error updating status in database:', error);
      throw error;
    }
  };

  // Rest der Funktionen bleibt unverändert...
  // (handleFileUpload, closeUploadModal, loadZendeskForCustomers, resetCustomerMonth, etc.)

const handleFileUpload = async (
    file: File | null, 
    isTestMode: boolean = false, 
    additionalFiles?: Record<string, File>, 
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string,
    confirmedTodos?: string[], // NEU: Array mit bestätigten To-Do IDs
    scheduledAtLocal?: string | null // Stufe 3: terminierter Mandantenmail-Versand (Berliner Wanduhrzeit)
  ) => {
    if (!currentFinalizationData) return;

    const { view, field } = currentFinalizationData;

    // FIX (Doppelversand): Sobald die E-Mail erfolgreich versendet wurde, wird dieses
    // Flag gesetzt. Schlägt danach ein nachgelagerter Schritt (z.B. das Status-Update)
    // fehl, darf der Fehler NICHT als generisches "Verarbeitung fehlgeschlagen" erscheinen,
    // das den Nutzer zum erneuten Senden verleitet. Stattdessen wird ein eindeutig
    // markierter Fehler geworfen (MAIL_SENT_STATUS_FAILED), den das UploadModal erkennt.
    let mailSent = false;

    try {
      // Absender beim Klick bestimmen (siehe ermittleAbsender). Nur wenn
      // eine Mail rausgeht; bei kein_versand ohne Testmodus bleibt alles wie bisher.
      if (view.customer.versandmethode !== 'kein_versand' || isTestMode) {
        senderIdentity = await ermittleAbsender(senderIdentity);
      }

      // NEUE VALIDIERUNG: Prüfe Sender-Foto BEVOR E-Mail-Versand
      if (senderIdentity) {
        const photoValidation = await staffService.validateSenderPhoto(senderIdentity.id);
        if (!photoValidation.valid) {
          throw new Error(`E-Mail-Versand nicht möglich: ${photoValidation.error}`);
        }
      }

      // NEU: Speichere bestätigte To-Dos in der Datenbank
      if (confirmedTodos && confirmedTodos.length > 0) {
        if (view.status) {
          // Update bestehenden Status mit bestätigten To-Dos
          const { error: updateError } = await supabase
            .from('customer_monthly_status')
            .update({
              confirmed_todos: confirmedTodos
            })
            .eq('id', view.status.id);
          
          if (updateError) {
            console.error('Error updating confirmed todos:', updateError);
          }
        } else {
          // Erstelle neuen Status-Eintrag falls noch keiner existiert
          const { data: createdStatus, error: createError } = await supabase
            .from('customer_monthly_status')
            .insert([{
              customer_id: view.customer.id,
              year: view.currentYear,
              month: view.currentMonth,
              lohnlauf_1_completed: false,
              lohnlauf_2_completed: false,
              lohnsteuer_completed: false,
              sv_beitraege_completed: false,
              fully_completed: false,
              confirmed_todos: confirmedTodos
            }])
            .select()
            .single();
          
          if (createError) {
            console.error('Error creating status for confirmed todos:', createError);
          } else if (createdStatus) {
            // FIX (Doppel-INSERT): Die soeben angelegte Zeile in view.status
            // zurückschreiben. Sonst sieht updateStatusInDatabase weiterhin einen
            // leeren Status und macht einen zweiten INSERT auf denselben Monat, den
            // die Unique-Regel (customer_id, year, month) abweist -> die E-Mail wäre
            // dann raus, der Status aber nicht gesetzt.
            view.status = createdStatus as CustomerMonthlyStatus;
          }
        }
      }

      // Extract user name from token if available
      let userName = 'XTAX Team';
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userName = payload.name || 'XTAX Team';
        } catch (e) {
          console.warn('Could not parse user token for name');
        }
      }

      // Stufe 3: Innenidentität (auslösende Person) für den terminierten
      // Versand ermitteln — gleiche bewährte Quelle wie das Snooze-Feature
      // (authToken + xtax_staff). Ohne Terminierung entfällt der Lookup.
      // Schlägt er trotz Terminierung fehl, lehnt der EmailService den
      // Request mit klarer Fehlermeldung ab (kein stiller Fallback).
      let erstelltVon: { name: string; email: string } | undefined = undefined;
      if (scheduledAtLocal && !isTestMode) {
        const userInfo = await getCurrentUserInfo();
        if (userInfo) {
          erstelltVon = { name: userInfo.name, email: userInfo.email };
        }
      }

      // Send notification using the scalable service
      const success = await notificationService.sendLohnauswertung(
        view.customer,
        view.currentMonth,
        view.currentYear,
        userName,
        file || undefined,
        isTestMode,
        additionalFiles,
        senderIdentity,
        customMessage,
        scheduledAtLocal ?? null,
        erstelltVon
      );

      if (!success) {
        throw new Error('E-Mail konnte nicht versendet werden');
      }

      // FIX (Doppelversand): Ab hier ist die E-Mail nachweislich raus —
      // bzw. im Stufe-3-Geplant-Fall nachweislich eingefroren. In beiden
      // Fällen darf ein Folgefehler nicht zum erneuten Senden verleiten.
      mailSent = true;

      // TESTMODUS-FIX: Nur im Normal-Modus Datenbank aktualisieren
      if (!isTestMode) {
        // FIX (Token-Rotations-Race): Schlägt das Status-Update mit einem Auth-/401-Fehler
        // fehl (Frontend-Token wurde im Rotationsfenster entwertet), wird die Session einmal
        // aktiv erneuert und das Update genau einmal wiederholt. Kann die Session nicht
        // erneuert werden, wird nicht wiederholt und der ursprüngliche Fehler weitergereicht.
        // Bei jedem anderen Fehler wird ebenfalls weitergereicht.
        try {
          await updateStatusInDatabase(view, field, true, true);
        } catch (statusError) {
          if (isAuthError(statusError)) {
            console.warn('[Finalisierung] Status-Update mit Auth-Fehler, erneuere Session und wiederhole einmalig');
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('[Finalisierung] Session-Refresh fehlgeschlagen, Status-Update wird nicht wiederholt:', refreshError);
              throw statusError;
            }
            await updateStatusInDatabase(view, field, true, true);
          } else {
            throw statusError;
          }
        }
      }

      // === PDF-Analyse: Abrechnungsdaten extrahieren (non-blocking) ===
      let extractedArbeitnehmer = 0;
      if (file && !isTestMode) {
        try {
          console.log('[Finalisierung] Starte PDF-Analyse für Abrechnungsdaten...');
          const analysisResult = await payrollPdfAnalyzerService.analyzePdf(
            file,
            view.currentMonth,
            view.currentYear
          );

          if (analysisResult.success && analysisResult.data) {
            const saveResult = await payrollDataService.savePayrollData({
              customerId: view.customer.id,
              year: view.currentYear,
              month: view.currentMonth,
              anzahlArbeitnehmer: analysisResult.data.anzahlArbeitnehmer,
              anzahlArbeitnehmerMitEntgelt: analysisResult.data.anzahlArbeitnehmerMitEntgelt,
              hasNextMonthBeitragsnachweis: analysisResult.data.hasNextMonthBeitragsnachweis,
              nextMonth: analysisResult.data.nextMonth,
              nextMonthYear: analysisResult.data.nextMonthYear,
              sourceFilename: file.name
            });

            if (saveResult.success) {
              console.log('[Finalisierung] Abrechnungsdaten erfolgreich gespeichert:', analysisResult.data);
            } else {
              console.warn('[Finalisierung] Abrechnungsdaten konnten nicht gespeichert werden:', saveResult.error);
            }

            // Folgemonats-BN erkannt → SV-Beiträge im Folgemonat automatisch als erledigt markieren
            if (analysisResult.data.hasNextMonthBeitragsnachweis && analysisResult.data.nextMonth && analysisResult.data.nextMonthYear) {
              const nextMonth = analysisResult.data.nextMonth;
              const nextMonthYear = analysisResult.data.nextMonthYear;

              console.log(`[Finalisierung] Folgemonats-BN erkannt → markiere sv_beitraege_completed für ${nextMonth}/${nextMonthYear}`);

              // Prüfe ob bereits ein Status-Eintrag für den Folgemonat existiert
              const { data: existingStatus } = await supabase
                .from('customer_monthly_status')
                .select('*')
                .eq('customer_id', view.customer.id)
                .eq('year', nextMonthYear)
                .eq('month', nextMonth)
                .maybeSingle();

              if (existingStatus) {
                // Update bestehenden Status
                await supabase
                  .from('customer_monthly_status')
                  .update({
                    sv_beitraege_completed: true,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', existingStatus.id);
              } else {
                // Neuen Status-Eintrag erstellen
                await supabase
                  .from('customer_monthly_status')
                  .insert([{
                    customer_id: view.customer.id,
                    year: nextMonthYear,
                    month: nextMonth,
                    lohnlauf_1_completed: false,
                    lohnlauf_2_completed: false,
                    lohnsteuer_completed: false,
                    sv_beitraege_completed: true,
                    fully_completed: false
                  }]);
              }

              // Lokalen State aktualisieren damit die View sofort reagiert
              setCustomerDeadlineViews(prevViews =>
                prevViews.map(prevView => {
                  if (prevView.customer.id === view.customer.id && prevView.currentMonth === nextMonth && prevView.currentYear === nextMonthYear) {
                    const updatedStatus = {
                      ...(prevView.status || {
                        id: '',
                        customer_id: view.customer.id,
                        year: nextMonthYear,
                        month: nextMonth,
                        lohnlauf_1_completed: false,
                        lohnlauf_2_completed: false,
                        lohnsteuer_completed: false,
                        fully_completed: false
                      }),
                      sv_beitraege_completed: true
                    } as CustomerMonthlyStatus;

                    return {
                      ...prevView,
                      status: updatedStatus,
                      isOverdue: calculateIsOverdue(
                        prevView.deadlines,
                        updatedStatus,
                        prevView.customer,
                        prevView.individualDeadlines,
                        prevView.nachzuegler,
                        laufFertigmeldungen(prevView),
                        spezialfristFertigmeldungen(prevView),
                        prevView.spezialfristNachzuegler,
                        prevView.lohnscheinRelativ,
                        prevView.individualNachzuegler
                      )
                    };
                  }
                  return prevView;
                })
              );

              console.log(`[Finalisierung] SV-Beiträge für ${nextMonth}/${nextMonthYear} automatisch als erledigt markiert`);
            }

            // Arbeitnehmeranzahl für Rechnungsentwurf merken
            if (analysisResult.data.anzahlArbeitnehmer && analysisResult.data.anzahlArbeitnehmer > 0) {
              extractedArbeitnehmer = analysisResult.data.anzahlArbeitnehmer;
            }
          } else {
            console.warn('[Finalisierung] PDF-Analyse nicht erfolgreich:', analysisResult.error);
          }
        } catch (analysisError) {
          // PDF-Analyse darf die Finalisierung nicht blockieren
          console.warn('[Finalisierung] PDF-Analyse fehlgeschlagen (non-blocking):', analysisError);
        }
      }
      // === Ende PDF-Analyse ===

      // === Rechnungsentwurf immer erstellen (non-blocking) ===
      let createdInvoiceId: string | null = null;
      if (!isTestMode) {
        try {
          const draftResult = await invoiceService.createDraft({
            customerId: view.customer.id,
            year: view.currentYear,
            month: view.currentMonth,
            anzahlArbeitnehmer: extractedArbeitnehmer
          });

          if (draftResult.success) {
            console.log(`[Finalisierung] Rechnungsentwurf erstellt: ${draftResult.invoiceId}`);
            createdInvoiceId = draftResult.invoiceId || null;
          } else {
            console.warn('[Finalisierung] Rechnungsentwurf konnte nicht erstellt werden:', draftResult.error);
            alert(`Finalisierung erfolgreich abgeschlossen, aber der Rechnungsentwurf konnte nicht erstellt werden:\n\n${draftResult.error || 'Unbekannter Fehler'}`);
          }
        } catch (draftError) {
          console.warn('[Finalisierung] Rechnungsentwurf fehlgeschlagen (non-blocking):', draftError);
        }
      }
      // === Ende Rechnungsentwurf ===

      // === NEU (Stufe 2): Pending-Task für DATEV-Kunden anlegen ===
      // Wenn der Kunde eine DATEV-Anbindung hat (beraternummer + mandantennummer),
      // legen wir einen Task an, der wartet bis das LOJO im DATEV-Spiegel ankommt.
      // Sobald es da ist, korrigiert der invoiceAutofillHandler die Arbeitnehmer-
      // anzahl im Rechnungsentwurf automatisch (falls sie vom Upload-PDF abweicht).
      // Für Kunden ohne DATEV-Anbindung passiert nichts - der bestehende Flow
      // bleibt pixel-identisch.
      //
      // NEU (Task-Rauschen-Fix): Zusätzlich wird geprüft, ob der Kunde tatsächlich
      // bei DATEV autorisiert ist (gültige datev_auth-Zeile). Zuvor genügte eine
      // hinterlegte Berater-/Mandantennummer — dadurch entstanden bei nicht
      // gesyncten Kunden Tasks, deren LOJO nie kam und die zwangsläufig
      // eskalierten. Für autorisierte Kunden bleibt das Verhalten unverändert.
      if (!isTestMode && createdInvoiceId && view.customer.beraternummer && view.customer.mandantennummer
          && await datevPendingTaskService.isCustomerDatevAuthorized(view.customer.beraternummer, view.customer.mandantennummer)) {
        try {
          const taskResult = await datevPendingTaskService.createTask({
            customerId: view.customer.id,
            year: view.currentYear,
            month: view.currentMonth,
            taskType: 'invoice_draft_autofill',
            requiredDocuments: ['LOJO'],
            context: {
              invoice_id: createdInvoiceId,
              initial_arbeitnehmer_count: extractedArbeitnehmer
            }
          });
          if (taskResult.created) {
            console.log(`[Finalisierung] Pending-Task invoice_draft_autofill angelegt für Rechnung ${createdInvoiceId}`);
          } else if (taskResult.taskId) {
            console.log(`[Finalisierung] Pending-Task invoice_draft_autofill existiert bereits (Task ${taskResult.taskId})`);
          } else if (taskResult.error) {
            console.warn(`[Finalisierung] Pending-Task-Anlage fehlgeschlagen:`, taskResult.error);
          }
        } catch (taskErr) {
          console.warn('[Finalisierung] Pending-Task-Anlage fehlgeschlagen (non-blocking):', taskErr);
        }
      }
      // === Ende Pending-Task ===

      setCurrentFinalizationData(null);
      setShowUploadModal(false);

    } catch (error) {
      console.error('Error during finalization:', error);
      // FIX (Doppelversand): War die E-Mail bereits raus, darf der Fehler nicht zum
      // erneuten Versand verleiten. Eindeutig markierter Fehler für das UploadModal.
      if (mailSent) {
        throw new Error('MAIL_SENT_STATUS_FAILED');
      }
      throw error;
    }
  };
    
  const closeUploadModal = () => {
    setCurrentFinalizationData(null);
    setShowUploadModal(false);
  };

  // === NEU (Stufe 3.1): xtax_portal Release-Flow ===
  // Wird vom ReleaseModal aufgerufen, wenn der Mitarbeiter "Bereitstellen" klickt.
  // Workflow:
  //   1. Sender-Foto validieren (analog handleFileUpload)
  //   1b. Versandzeitpunkt bestimmen; bei Sofortversand die Bestätigung einem
  //       angemeldeten Mitarbeiter zuordnen (vor jedem Upload, damit bei einem
  //       Abbruch keine Staging-Dateien liegen bleiben)
  //   2. confirmedTodos in DB speichern
  //   3. Manuelle Anhänge in Staging-Bereich hochladen
  //      (staging/{customer_id}/{task_uuid}/{filename} im secure-customer-documents-Bucket)
  //   4. Pending-Task xtax_portal_release anlegen mit den Staging-Pfaden und
  //      dem Versandmodus. Edge-Handler triggert später die Paket-Anlage und
  //      den Mail-Versand: im Modus 'nach_bereitstellung' erst nach dem
  //      maßgeblichen DATEV-Abruf, im Modus 'sofort' sobald LOJO im Spiegel ist
  //   5. Status auf finalisiert setzen
  //   6. Rechnungsentwurf + invoice_draft_autofill-Task anlegen
  //      (orthogonal zum Versand, gleiche Logik wie bei manuellem Upload)
  // Bei Fehler: Cleanup bereits hochgeladener Staging-Files.
  const handleRelease = async (
    contactId: string,
    additionalFiles?: Record<string, File>,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string,
    confirmedTodos?: string[],
    versandSteuerung?: ReleaseVersandSteuerung
  ) => {
    if (!currentFinalizationData) return;

    const { view, field } = currentFinalizationData;

    try {
      // 0) Absender beim Klick bestimmen (siehe ermittleAbsender)
      senderIdentity = await ermittleAbsender(senderIdentity);

      // 1) Sender-Foto-Validierung
      if (senderIdentity) {
        const photoValidation = await staffService.validateSenderPhoto(senderIdentity.id);
        if (!photoValidation.valid) {
          throw new Error(`Bereitstellung nicht möglich: ${photoValidation.error}`);
        }
      }

      // 1b) Versandzeitpunkt. Ohne Angabe gilt der sichere Standard: Versand
      //     nach der nächsten DATEV-Bereitstellung. Ein Sofortversand wird mit
      //     Name, E-Mail und Zeitpunkt der Bestätigung am Task dokumentiert.
      const versandModus = versandSteuerung?.modus ?? 'nach_bereitstellung';
      let sofortversandBestaetigung: Record<string, unknown> | null = null;
      if (versandModus === 'sofort') {
        const userInfo = await getCurrentUserInfo();
        if (!userInfo || !userInfo.email) {
          throw new Error('Sofortversand nicht möglich: Die Bestätigung konnte keinem angemeldeten Mitarbeiter zugeordnet werden. Bitte melde dich neu an oder wähle den Versand nach der nächsten DATEV-Bereitstellung.');
        }
        sofortversandBestaetigung = {
          bestaetigt_von_name: userInfo.name,
          bestaetigt_von_email: userInfo.email,
          bestaetigt_am: new Date().toISOString(),
          letzte_bereitstellung: versandSteuerung?.letzteBereitstellung ?? null
        };
      }

      // 2) confirmedTodos persistieren
      if (confirmedTodos && confirmedTodos.length > 0) {
        if (view.status) {
          const { error: updateError } = await supabase
            .from('customer_monthly_status')
            .update({ confirmed_todos: confirmedTodos })
            .eq('id', view.status.id);
          if (updateError) {
            console.error('Error updating confirmed todos:', updateError);
          }
        } else {
          const { data: createdStatus, error: createError } = await supabase
            .from('customer_monthly_status')
            .insert([{
              customer_id: view.customer.id,
              year: view.currentYear,
              month: view.currentMonth,
              lohnlauf_1_completed: false,
              lohnlauf_2_completed: false,
              lohnsteuer_completed: false,
              sv_beitraege_completed: false,
              fully_completed: false,
              confirmed_todos: confirmedTodos
            }])
            .select()
            .single();
          if (createError) {
            console.error('Error creating status for confirmed todos:', createError);
          } else if (createdStatus) {
            // FIX (Doppel-INSERT): Die soeben angelegte Zeile in view.status
            // zurückschreiben, damit updateStatusInDatabase in den UPDATE-Pfad geht
            // statt einen zweiten INSERT auf denselben Monat zu versuchen, den die
            // Unique-Regel (customer_id, year, month) abweisen würde.
            view.status = createdStatus as CustomerMonthlyStatus;
          }
        }
      }

      // 3) Staging-Upload manueller Anhänge
      const taskUuid = crypto.randomUUID();
      const stagingAttachmentPaths: string[] = [];

      if (additionalFiles && Object.keys(additionalFiles).length > 0) {
        try {
          for (const file of Object.values(additionalFiles)) {
            const sanitizedFilename = file.name
              .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
              .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
              .replace(/ß/g, 'ss')
              .replace(/\s+/g, '_')
              .replace(/[^a-zA-Z0-9._-]/g, '');
            const stagingPath = `staging/${view.customer.id}/${taskUuid}/${sanitizedFilename}`;

            const { error: uploadErr } = await supabase.storage
              .from('secure-customer-documents')
              .upload(stagingPath, file, { upsert: false });

            if (uploadErr) {
              throw new Error(`Staging-Upload fehlgeschlagen (${file.name}): ${uploadErr.message}`);
            }
            stagingAttachmentPaths.push(stagingPath);
          }
        } catch (stagingErr) {
          // Cleanup bereits hochgeladener Files, dann Re-throw
          if (stagingAttachmentPaths.length > 0) {
            await supabase.storage
              .from('secure-customer-documents')
              .remove(stagingAttachmentPaths);
          }
          throw stagingErr;
        }
      }

      // 4) Sender-Info für Edge-Handler ableiten (Fallback auf lohn@xtax.de)
      const senderEmail = senderIdentity?.email || 'lohn@xtax.de';
      const senderName = senderIdentity?.fullName || 'XTAX Team';

      // 5) Pending-Task xtax_portal_release anlegen
      const taskResult = await datevPendingTaskService.createTask({
        customerId: view.customer.id,
        year: view.currentYear,
        month: view.currentMonth,
        taskType: 'xtax_portal_release',
        requiredDocuments: ['LOJO'],
        context: {
          task_uuid: taskUuid,
          contact_id: contactId,
          sender_email: senderEmail,
          sender_name: senderName,
          staging_attachment_paths: stagingAttachmentPaths,
          custom_message: customMessage || null,
          versand_modus: versandModus,
          sofortversand_bestaetigung: sofortversandBestaetigung
        }
      });

      if (!taskResult.created && !taskResult.taskId) {
        // Cleanup, weil Pending-Task nicht angelegt werden konnte
        if (stagingAttachmentPaths.length > 0) {
          await supabase.storage
            .from('secure-customer-documents')
            .remove(stagingAttachmentPaths);
        }
        throw new Error(`Pending-Task konnte nicht angelegt werden: ${taskResult.error || 'unbekannt'}`);
      }

      if (taskResult.created) {
        console.log(`[Finalisierung] Pending-Task xtax_portal_release angelegt für ${view.customer.company} ${view.currentMonth}/${view.currentYear} (Versandmodus: ${versandModus})`);
      } else if (taskResult.taskId) {
        // Existing-Task-Fall: Staging-Files könnten verwaisen, da der bestehende
        // Task seine eigenen Pfade hat. Pragmatisch entfernen, damit nichts liegen bleibt.
        if (stagingAttachmentPaths.length > 0) {
          await supabase.storage
            .from('secure-customer-documents')
            .remove(stagingAttachmentPaths);
        }
        console.log(`[Finalisierung] Pending-Task xtax_portal_release existiert bereits (Task ${taskResult.taskId}) — Staging-Files verworfen`);
      }

      // 6) Status auf finalisiert setzen
      await updateStatusInDatabase(view, field, true, true);

      // 7) Rechnungsentwurf erstellen (analog zu handleFileUpload, non-blocking)
      let createdInvoiceId: string | null = null;
      try {
        const draftResult = await invoiceService.createDraft({
          customerId: view.customer.id,
          year: view.currentYear,
          month: view.currentMonth,
          anzahlArbeitnehmer: 0  // wird via invoice_draft_autofill-Task korrigiert sobald LOJO da ist
        });

        if (draftResult.success) {
          console.log(`[Finalisierung] Rechnungsentwurf erstellt: ${draftResult.invoiceId}`);
          createdInvoiceId = draftResult.invoiceId || null;
        } else {
          console.warn('[Finalisierung] Rechnungsentwurf konnte nicht erstellt werden:', draftResult.error);
          alert(`Finalisierung erfolgreich abgeschlossen, aber der Rechnungsentwurf konnte nicht erstellt werden:\n\n${draftResult.error || 'Unbekannter Fehler'}`);
        }
      } catch (draftError) {
        console.warn('[Finalisierung] Rechnungsentwurf fehlgeschlagen (non-blocking):', draftError);
      }

      // 8) invoice_draft_autofill-Pending-Task (orthogonal zu xtax_portal_release)
      // Kunde hat per Definition DATEV-Anbindung (versandmethode='xtax_portal'),
      // also legen wir den Autofill-Task immer an.
      if (createdInvoiceId) {
        try {
          const autofillResult = await datevPendingTaskService.createTask({
            customerId: view.customer.id,
            year: view.currentYear,
            month: view.currentMonth,
            taskType: 'invoice_draft_autofill',
            requiredDocuments: ['LOJO'],
            context: {
              invoice_id: createdInvoiceId,
              initial_arbeitnehmer_count: 0
            }
          });
          if (autofillResult.created) {
            console.log(`[Finalisierung] Pending-Task invoice_draft_autofill angelegt für Rechnung ${createdInvoiceId}`);
          } else if (autofillResult.taskId) {
            console.log(`[Finalisierung] Pending-Task invoice_draft_autofill existiert bereits (Task ${autofillResult.taskId})`);
          }
        } catch (taskErr) {
          console.warn('[Finalisierung] invoice_draft_autofill-Task-Anlage fehlgeschlagen (non-blocking):', taskErr);
        }
      }

      setCurrentFinalizationData(null);
      setShowReleaseModal(false);
    } catch (error) {
      console.error('Error during xtax_portal release:', error);
      throw error;
    }
  };

  const closeReleaseModal = () => {
    setCurrentFinalizationData(null);
    setShowReleaseModal(false);
  };
  // === Ende xtax_portal Release-Flow ===

  // === Lohnschein-Versand (Stufe 3.2, Baustein C) ===
  // Wird vom LohnscheinVersandModal nach erfolgreichem Versand (oder über
  // "Ohne Versand als erledigt markieren") aufgerufen: erledigt die Frist
  // über den bestehenden Statuspfad — kein Finalisierungs-Flag.
  // Zwei-Lauf-Modell: Fertigmeldung eines Laufs in lohnschein_versand_lauf_status
  // setzen/zuruecksetzen und den lokalen View aktualisieren (Kachel gruen/aus).
  //
  // NACHZÜGLER: arbeitsjahr/arbeitsmonat bezeichnen den Monat, ZU DEM die Frist
  // gehört. Default ist der aktuelle Arbeitsmonat, damit alle bisherigen Aufrufe
  // unverändert funktionieren. Bei einem Nachzügler wird der abgeschlossene
  // Monat übergeben; dann verschwindet die Nachzügler-Kachel statt eine Kachel
  // des laufenden Monats zu färben.
  const setLohnscheinLaufCompleted = async (
    view: CustomerDeadlineView,
    lohnlauf: 1 | 2,
    completed: boolean,
    arbeitsjahr: number = view.currentYear,
    arbeitsmonat: number = view.currentMonth
  ) => {
    try {
      const { error } = await supabase
        .from('lohnschein_versand_lauf_status')
        .upsert({
          customer_id: view.customer.id,
          arbeitsjahr,
          arbeitsmonat,
          lohnlauf,
          completed,
          completed_at: completed ? new Date().toISOString() : null
        }, { onConflict: 'customer_id,arbeitsjahr,arbeitsmonat,lohnlauf' });
      if (error) throw error;

      const istArbeitsmonat = arbeitsjahr === view.currentYear && arbeitsmonat === view.currentMonth;
      const feld = lohnlauf === 1 ? 'lohnscheinCompleted1' : 'lohnscheinCompleted2';

      setCustomerDeadlineViews(prevViews =>
        prevViews.map(prevView => {
          if (prevView.customer.id !== view.customer.id) return prevView;

          // NACHZÜGLER-Zweig: Kachel entfernen. Weil dadurch eine überfällige
          // Frist wegfällt, wird die Überfälligkeit hier neu bewertet.
          if (!istArbeitsmonat) {
            const restNachzuegler = (prevView.nachzuegler || []).filter(n =>
              !(n.arbeitsjahr === arbeitsjahr && n.arbeitsmonat === arbeitsmonat && n.lohnlauf === lohnlauf)
            );
            return {
              ...prevView,
              nachzuegler: restNachzuegler,
              isOverdue: calculateIsOverdue(
                prevView.deadlines,
                prevView.status,
                prevView.customer,
                prevView.individualDeadlines,
                restNachzuegler,
                laufFertigmeldungen(prevView),
                spezialfristFertigmeldungen(prevView),
                prevView.spezialfristNachzuegler,
                prevView.lohnscheinRelativ,
                prevView.individualNachzuegler
              )
            };
          }

          // Aktueller Arbeitsmonat: Fertigmeldung im eigenen View-Feld setzen.
          // Das Status-Objekt wird bewusst nicht angefasst.
          return { ...prevView, [feld]: completed };
        })
      );
    } catch (e) {
      console.error('Fehler beim Setzen der Lohnschein-Lauf-Fertigmeldung:', e);
      alert('Fehler beim Aktualisieren der Lohnschein-Fertigmeldung');
    }
  };

  // === Periodenbezogene Spezialfristen: Erledigung setzen/zuruecknehmen ===
  // Schreibt den Erledigungsvermerk nach customer_periodenfrist_status, also an
  // die ABRECHNUNGSPERIODE statt an den Arbeitsmonat. Ohne explizite Periode
  // wird die Periode der Kachel dieser View genommen; bei einem Nachzuegler
  // uebergibt der Aufrufer die Periode des abgeschlossenen Monats.
  //
  // Ein Zuruecknehmen loescht die Zeile nicht, sondern setzt completed=false.
  // So bleibt nachvollziehbar, dass die Frist einmal erledigt war.
  const setSpezialfristCompleted = async (
    view: CustomerDeadlineView,
    fristArt: SpezialfristArt,
    completed: boolean,
    periodeJahr?: number,
    periodeMonat?: number
  ) => {
    const viewPeriode = fristArt === 'oppm_qr_versand'
      ? view.oppmQrPeriode
      : view.monatsuebersichtQrPeriode;
    const jahr = periodeJahr ?? viewPeriode?.year;
    const monat = periodeMonat ?? viewPeriode?.month;
    if (jahr === undefined || monat === undefined) {
      console.warn('[Spezialfrist] Keine Periode ermittelbar - Erledigung nicht gesetzt');
      return;
    }

    try {
      const staffId = completed ? await getCurrentStaffId() : null;

      const { error } = await supabase
        .from('customer_periodenfrist_status')
        .upsert({
          customer_id: view.customer.id,
          frist_art: fristArt,
          periode_jahr: jahr,
          periode_monat: monat,
          completed,
          completed_at: completed ? new Date().toISOString() : null,
          completed_by: staffId,
          quelle: completed ? 'manuell' : null
        }, { onConflict: 'customer_id,frist_art,periode_jahr,periode_monat' });
      if (error) throw error;

      const istKachelPeriode = !!viewPeriode && viewPeriode.year === jahr && viewPeriode.month === monat;
      const feld = fristArt === 'oppm_qr_versand' ? 'oppmQrCompleted' : 'monatsuebersichtQrCompleted';

      setCustomerDeadlineViews(prevViews =>
        prevViews.map(prevView => {
          if (prevView.customer.id !== view.customer.id) return prevView;

          // NACHZÜGLER-Zweig: Kachel entfernen. Weil dadurch eine ueberfaellige
          // Frist wegfaellt, wird die Ueberfaelligkeit hier neu bewertet.
          if (!istKachelPeriode) {
            const restNachzuegler = (prevView.spezialfristNachzuegler || []).filter(n =>
              !(n.frist_art === fristArt && n.periode.year === jahr && n.periode.month === monat)
            );
            return {
              ...prevView,
              spezialfristNachzuegler: restNachzuegler,
              isOverdue: calculateIsOverdue(
                prevView.deadlines,
                prevView.status,
                prevView.customer,
                prevView.individualDeadlines,
                prevView.nachzuegler,
                laufFertigmeldungen(prevView),
                spezialfristFertigmeldungen(prevView),
                restNachzuegler,
                prevView.lohnscheinRelativ,
                prevView.individualNachzuegler
              )
            };
          }

          // Kachel des Arbeitsmonats: Erledigung im eigenen View-Feld setzen.
          // Das Statusobjekt wird bewusst nicht angefasst.
          const aktualisiert = { ...prevView, [feld]: completed };
          return {
            ...aktualisiert,
            isOverdue: calculateIsOverdue(
              aktualisiert.deadlines,
              aktualisiert.status,
              aktualisiert.customer,
              aktualisiert.individualDeadlines,
              aktualisiert.nachzuegler,
              laufFertigmeldungen(aktualisiert),
              spezialfristFertigmeldungen(aktualisiert),
              aktualisiert.spezialfristNachzuegler,
              aktualisiert.lohnscheinRelativ,
              aktualisiert.individualNachzuegler
            )
          };
        })
      );
    } catch (e) {
      console.error('Fehler beim Setzen der Spezialfrist-Erledigung:', e);
      alert('Fehler beim Aktualisieren der Frist');
    }
  };

  /**
   * NACHZÜGLER SPEZIALFRISTEN: Klick auf eine Nachzuegler-Kachel.
   *
   * Eigener Einstieg statt toggleStatus, damit der Status des laufenden
   * Arbeitsmonats unberuehrt bleibt. Das Modal bekommt den Monat des
   * Nachzuegers, damit Task-Anlage und Erledigung auf dessen Periode wirken.
   */
  const openSpezialfristNachzuegler = (
    view: CustomerDeadlineView,
    nachzuegler: SpezialfristNachzuegler
  ) => {
    if (nachzuegler.frist_art === 'oppm_qr_versand') {
      setOppmQrVersandData({ view, field: 'oppm_qr_versand_completed', nachzuegler });
      setShowOppmQrModal(true);
    } else {
      setMonatsuebersichtQrVersandData({ view, field: 'monatsuebersicht_qr_versand_completed', nachzuegler });
      setShowMonatsuebersichtQrModal(true);
    }
  };

  /**
   * NACHZÜGLER INDIVIDUELLE FRISTEN: Klick auf eine Nachzügler-Kachel.
   *
   * Erledigt die Frist für den abgeschlossenen Monat, also im Monatsstatus
   * dieses Monats (individual_deadlines_status), nicht im laufenden
   * Arbeitsmonat. Eigener Einstieg statt toggleStatus, damit der Status des
   * laufenden Arbeitsmonats unberührt bleibt.
   *
   * Der bestehende Erledigungsstand wird vor dem Schreiben frisch gelesen, damit
   * zwischenzeitlich gesetzte Häkchen anderer Fristen dieses Monats nicht
   * überschrieben werden. Danach verschwindet die Kachel, und die
   * Überfälligkeit wird neu bewertet.
   */
  const erledigeIndividualNachzuegler = async (
    view: CustomerDeadlineView,
    nachzuegler: IndividualNachzuegler
  ) => {
    const ladeFeld = getIndividualNachzueglerKey(nachzuegler) as StatusField;
    setButtonLoading(view.customer.id, ladeFeld, true);

    try {
      const { data: statusZeile, error: leseFehler } = await supabase
        .from('customer_monthly_status')
        .select('id, individual_deadlines_status')
        .eq('id', nachzuegler.status_id)
        .maybeSingle();

      if (leseFehler) throw leseFehler;
      if (!statusZeile) {
        throw new Error('Monatsstatus des abgeschlossenen Monats nicht gefunden');
      }

      const neuerErledigungsstand = {
        ...(statusZeile.individual_deadlines_status || {}),
        [nachzuegler.deadline_id]: true
      };

      const { error: schreibFehler } = await supabase
        .from('customer_monthly_status')
        .update({
          individual_deadlines_status: neuerErledigungsstand,
          updated_at: new Date().toISOString()
        })
        .eq('id', nachzuegler.status_id);

      if (schreibFehler) throw schreibFehler;

      setCustomerDeadlineViews(prevViews =>
        prevViews.map(prevView => {
          if (prevView.customer.id !== view.customer.id) return prevView;

          const restNachzuegler = (prevView.individualNachzuegler || []).filter(n =>
            !(n.deadline_id === nachzuegler.deadline_id && n.arbeitsjahr === nachzuegler.arbeitsjahr && n.arbeitsmonat === nachzuegler.arbeitsmonat)
          );
          return {
            ...prevView,
            individualNachzuegler: restNachzuegler,
            isOverdue: calculateIsOverdue(
              prevView.deadlines,
              prevView.status,
              prevView.customer,
              prevView.individualDeadlines,
              prevView.nachzuegler,
              laufFertigmeldungen(prevView),
              spezialfristFertigmeldungen(prevView),
              prevView.spezialfristNachzuegler,
              prevView.lohnscheinRelativ,
              restNachzuegler
            )
          };
        })
      );
    } catch (e) {
      console.error('Fehler beim Erledigen der Nachzügler-Frist:', e);
      alert('Fehler beim Erledigen der Frist');
    } finally {
      setButtonLoading(view.customer.id, ladeFeld, false);
    }
  };

  const handleLohnscheinVersandSuccess = async (setzeBoolean: boolean = true) => {
    if (!lohnscheinVersandData) return;
    const { view, lohnlauf, arbeitsjahr, arbeitsmonat, istNachzuegler } = lohnscheinVersandData;
    if (setzeBoolean) {
      // "Ohne Versand als erledigt markieren": Fertigmeldung dieses Laufs setzen —
      // für den Monat, zu dem die Frist gehört (Arbeitsmonat oder Nachzügler).
      await setLohnscheinLaufCompleted(view, lohnlauf, true, arbeitsjahr, arbeitsmonat);
    } else if (istNachzuegler) {
      // Auftrag angelegt (Weg A) auf einer Nachzügler-Kachel: nur diese Kachel
      // in den gelben Wartezustand setzen.
      setCustomerDeadlineViews(prevViews =>
        prevViews.map(prevView =>
          prevView.customer.id === view.customer.id
            ? {
                ...prevView,
                nachzuegler: (prevView.nachzuegler || []).map(n =>
                  (n.arbeitsjahr === arbeitsjahr && n.arbeitsmonat === arbeitsmonat && n.lohnlauf === lohnlauf)
                    ? { ...n, auftrag: { status: 'wartet', last_error: null } }
                    : n
                )
              }
            : prevView
        )
      );
    } else {
      // Auftrag angelegt (Weg A): nur die betroffene Lauf-Kachel gelb setzen.
      const feld = lohnlauf === 1 ? 'lohnscheinVersandAuftrag1' : 'lohnscheinVersandAuftrag2';
      setCustomerDeadlineViews(prevViews =>
        prevViews.map(prevView =>
          prevView.customer.id === view.customer.id
            ? { ...prevView, [feld]: { status: 'wartet', last_error: null } }
            : prevView
        )
      );
    }
  };

  const closeLohnscheinModal = () => {
    setLohnscheinVersandData(null);
    setShowLohnscheinModal(false);
  };
  // === Ende Lohnschein-Versand ===

  // === OPPM QR-Code-Versand ===
  // handleOppmQrRelease legt den Pending-Task OPPM_payslips_qr_code mit der
  // Abrechnungsperiode der Kachel an (der bestehende Handler baut das ZIP,
  // sobald LOBN im Spiegel ankommt). Die Frist wird NICHT hier erledigt -- das
  // macht der Edge-Handler nach dem Versand. Die Kachel geht auf den gelben
  // "beauftragt/wartend"-Zustand. handleOppmQrMarkDone erledigt die Frist
  // sofort ohne Versand.
  //
  // Bei einem Nachzuegler gilt dessen Periode, nicht die der laufenden Kachel.
  const handleOppmQrRelease = async (
    contactId: string,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string
  ) => {
    if (!oppmQrVersandData) return;
    const { view, nachzuegler } = oppmQrVersandData;

    // 0) Absender beim Klick bestimmen (siehe ermittleAbsender)
    senderIdentity = await ermittleAbsender(senderIdentity);

    // 1) Absender-Foto validieren (analog handleRelease)
    if (senderIdentity) {
      const photoValidation = await staffService.validateSenderPhoto(senderIdentity.id);
      if (!photoValidation.valid) {
        throw new Error(`Bereitstellung nicht möglich: ${photoValidation.error}`);
      }
    }

    const senderEmail = senderIdentity?.email || 'lohn@xtax.de';
    const senderName = senderIdentity?.fullName || 'XTAX Team';

    // 2) Pending-Task mit der Abrechnungsperiode der Kachel anlegen.
    // Der Handler baut und versendet das ZIP, sobald LOBN im Spiegel vorliegt.
    const periode = nachzuegler?.periode ?? view.oppmQrPeriode;
    if (!periode) {
      throw new Error('Auftrag konnte nicht angelegt werden: keine Abrechnungsperiode ermittelbar');
    }
    const taskResult = await datevPendingTaskService.createTask({
      customerId: view.customer.id,
      year: periode.year,
      month: periode.month,
      taskType: 'OPPM_payslips_qr_code',
      requiredDocuments: ['LOBN'],
      context: {
        contact_id: contactId,
        sender_email: senderEmail,
        sender_name: senderName,
        custom_message: customMessage || null
      }
    });

    if (!taskResult.created && !taskResult.taskId) {
      throw new Error(`Auftrag konnte nicht angelegt werden: ${taskResult.error || 'unbekannt'}`);
    }

    if (taskResult.created) {
      console.log(`[OPPM-QR] Pending-Task OPPM_payslips_qr_code angelegt für ${view.customer.company} Periode ${periode.month}/${periode.year}`);
    } else if (taskResult.taskId) {
      console.log(`[OPPM-QR] Pending-Task OPPM_payslips_qr_code existiert bereits (Task ${taskResult.taskId})`);
    }

    // 3) Frist NICHT erledigen -- der Edge-Handler setzt den Erledigungsvermerk
    // nach erfolgreichem Versand. Nur die betroffene Kachel auf gelb setzen.
    setCustomerDeadlineViews(prevViews =>
      prevViews.map(prevView => {
        if (prevView.customer.id !== view.customer.id) return prevView;
        if (nachzuegler) {
          return {
            ...prevView,
            spezialfristNachzuegler: (prevView.spezialfristNachzuegler || []).map(n =>
              (n.frist_art === 'oppm_qr_versand' && n.periode.year === periode.year && n.periode.month === periode.month)
                ? { ...n, auftrag: { status: 'pending', last_error: null } }
                : n
            )
          };
        }
        return { ...prevView, oppmQrVersandTask: { status: 'pending', last_error: null } };
      })
    );

    setOppmQrVersandData(null);
    setShowOppmQrModal(false);
  };

  const handleOppmQrMarkDone = async () => {
    if (!oppmQrVersandData) return;
    const { view, nachzuegler } = oppmQrVersandData;
    // "Ohne Versand als erledigt markieren": Erledigungsvermerk fuer die
    // Abrechnungsperiode setzen (lokaler State -> Kachel wird gruen bzw. die
    // Nachzuegler-Kachel verschwindet).
    await setSpezialfristCompleted(
      view,
      'oppm_qr_versand',
      true,
      nachzuegler?.periode.year,
      nachzuegler?.periode.month
    );
    setOppmQrVersandData(null);
    setShowOppmQrModal(false);
  };

  const closeOppmQrModal = () => {
    setOppmQrVersandData(null);
    setShowOppmQrModal(false);
  };
  // === Ende OPPM QR-Code-Versand ===

  // === OPPM Monatsuebersicht QR-Code-Versand ===
  // handleMonatsuebersichtQrRelease legt den Pending-Task OPPM_monatsuebersicht_qr_code
  // mit der Vormonats-Periode an (leere requiredDocuments -- die Faelligkeit
  // entscheidet manifestbasiert die Engine). Die Frist wird NICHT hier erledigt;
  // der Edge-Handler setzt monatsuebersicht_qr_versand_completed nach dem Versand.
  // Die Kachel geht auf gelb. handleMonatsuebersichtQrMarkDone erledigt sofort
  // ohne Versand ueber den bestehenden Statuspfad.
  const handleMonatsuebersichtQrRelease = async (
    contactId: string,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string
  ) => {
    if (!monatsuebersichtQrVersandData) return;
    const { view, nachzuegler } = monatsuebersichtQrVersandData;

    // Absender beim Klick bestimmen (siehe ermittleAbsender)
    senderIdentity = await ermittleAbsender(senderIdentity);

    if (senderIdentity) {
      const photoValidation = await staffService.validateSenderPhoto(senderIdentity.id);
      if (!photoValidation.valid) {
        throw new Error(`Bereitstellung nicht möglich: ${photoValidation.error}`);
      }
    }

    const senderEmail = senderIdentity?.email || 'lohn@xtax.de';
    const senderName = senderIdentity?.fullName || 'XTAX Team';

    const periode = nachzuegler?.periode ?? view.monatsuebersichtQrPeriode;
    if (!periode) {
      throw new Error('Auftrag konnte nicht angelegt werden: keine Abrechnungsperiode ermittelbar');
    }
    const taskResult = await datevPendingTaskService.createTask({
      customerId: view.customer.id,
      year: periode.year,
      month: periode.month,
      taskType: 'OPPM_monatsuebersicht_qr_code',
      requiredDocuments: [],
      // Fälligkeit kommt nicht aus dem DATEV-Spiegel, sondern aus dem
      // Abschluss-Manifest des externen Laufs. Deshalb bleibt requiredDocuments
      // leer und die Dokumenten-Pflichtprüfung des Services greift hier nicht.
      faelligkeitsQuelle: 'extern',
      context: {
        contact_id: contactId,
        sender_email: senderEmail,
        sender_name: senderName,
        custom_message: customMessage || null,
        mandantennummer: view.customer.mandantennummer || null
      }
    });

    if (!taskResult.created && !taskResult.taskId) {
      throw new Error(`Auftrag konnte nicht angelegt werden: ${taskResult.error || 'unbekannt'}`);
    }

    if (taskResult.created) {
      console.log(`[MUE-QR] Pending-Task OPPM_monatsuebersicht_qr_code angelegt für ${view.customer.company} Periode ${periode.month}/${periode.year}`);
    } else if (taskResult.taskId) {
      console.log(`[MUE-QR] Pending-Task OPPM_monatsuebersicht_qr_code existiert bereits (Task ${taskResult.taskId})`);
    }

    setCustomerDeadlineViews(prevViews =>
      prevViews.map(prevView => {
        if (prevView.customer.id !== view.customer.id) return prevView;
        if (nachzuegler) {
          return {
            ...prevView,
            spezialfristNachzuegler: (prevView.spezialfristNachzuegler || []).map(n =>
              (n.frist_art === 'monatsuebersicht_qr_versand' && n.periode.year === periode.year && n.periode.month === periode.month)
                ? { ...n, auftrag: { status: 'stempeln', last_error: null } }
                : n
            )
          };
        }
        return { ...prevView, monatsuebersichtQrVersandTask: { status: 'stempeln', last_error: null } };
      })
    );

    setMonatsuebersichtQrVersandData(null);
    setShowMonatsuebersichtQrModal(false);
  };

  const handleMonatsuebersichtQrMarkDone = async () => {
    if (!monatsuebersichtQrVersandData) return;
    const { view, nachzuegler } = monatsuebersichtQrVersandData;
    await setSpezialfristCompleted(
      view,
      'monatsuebersicht_qr_versand',
      true,
      nachzuegler?.periode.year,
      nachzuegler?.periode.month
    );
    setMonatsuebersichtQrVersandData(null);
    setShowMonatsuebersichtQrModal(false);
  };

  const closeMonatsuebersichtQrModal = () => {
    setMonatsuebersichtQrVersandData(null);
    setShowMonatsuebersichtQrModal(false);
  };
  // === Ende OPPM Monatsuebersicht QR-Code-Versand ===

  // ZENDESK-TICKETZAHLEN: Seit die Zahlen mit loadData aus dem Zwischenspeicher
  // kommen, fragt diese Funktion nur noch für Kunden live nach, für die noch
  // keine Zahl vorliegt. Für alle anderen tut sie nichts. Die Aufrufer im
  // Fristenboard (Aufklappen einer Gruppe, Suche, Treffer anderer
  // Sachbearbeiter) bleiben dadurch unverändert.
  const loadZendeskForCustomers = async (customerIds: string[]) => {
    if (customerIds.length === 0) return;

    try {
      // Finde Kunden mit Zendesk-Tags, für die noch keine Ticketzahl vorliegt
      const customersWithTags = Array.from(new Set(
        customerDeadlineViews
          .filter(view => customerIds.includes(view.customer.id) && view.customer.zendesk_tag && view.openTicketsCount === undefined)
          .map(view => view.customer.zendesk_tag!)
      ));

      if (customersWithTags.length === 0) return;

      // Fetch Zendesk ticket counts
      const ticketCounts = await fetchZendeskTickets(customersWithTags);
      const stand = new Date().toISOString();

      // Update views with ticket counts. Unbekannte Werte (null) werden nicht
      // übernommen, das Badge bleibt dann ausgeblendet statt eine falsche 0 zu zeigen.
      setCustomerDeadlineViews(prevViews => mitLiveTicketzahlen(prevViews, ticketCounts, stand));
    } catch (error) {
      console.error('Error loading Zendesk tickets for specific customers:', error);
    }
  };
      
  const resetCustomerMonth = async (view: CustomerDeadlineView) => {
    try {
      // Hole ALLE Status-Einträge für diesen Kunden
      const { data: allStatusData, error: fetchError } = await supabase
        .from('customer_monthly_status')
        .select('*')
        .eq('customer_id', view.customer.id)
        .order('year', { ascending: false })
        .order('month', { ascending: false });

      if (fetchError) {
        console.error('Error fetching status data:', fetchError);
        alert('Fehler beim Laden der Status-Daten');
        return;
      }

      if (!allStatusData || allStatusData.length === 0) {
        console.log('Keine Status-Einträge gefunden');
        return;
      }

      // Finde den neuesten Status-Eintrag
      const latestStatus = allStatusData[0];
      console.log('Lösche neuesten Status:', latestStatus.year, '/', latestStatus.month);

      // === NEU (Stufe 3.1): Pending-Tasks aufräumen VOR Status-Reset ===
      // Sicherheitskritisch: Wenn ein xtax_portal_release-Task aktiv ist
      // (LOJO noch nicht da) und der Monat zurückgesetzt wird, würde das
      // Paket trotzdem rausgehen sobald LOJO ankommt — der Mandant bekäme
      // eine Mail, obwohl die Finalisierung manuell zurückgenommen wurde.
      // Wir löschen daher alle aktiven Pending-Tasks (xtax_portal_release
      // UND invoice_draft_autofill) für (customer, year, month) plus die
      // dazugehörigen Staging-Files.
      //
      // Stufe 3.1 Folge-Patch (Issue 3): Auch fehlgeschlagene Tasks
      // (status=completed mit last_error) werden aufgeräumt, damit beim
      // Re-Finalize keine Garbage-Einträge in der Tabelle hängen bleiben.
      //
      // Bei Fehler: Reset wird abgebrochen, damit kein inkonsistenter
      // Zustand entsteht.
      try {
        const { data: allTasks, error: tasksFetchErr } = await supabase
          .from('datev_pending_tasks')
          .select('id, task_type, status, last_error, context')
          .eq('customer_id', view.customer.id)
          .eq('year', latestStatus.year)
          .eq('month', latestStatus.month);

        if (tasksFetchErr) {
          console.error('[Reset] Pending-Tasks-Lookup fehlgeschlagen:', tasksFetchErr);
          alert(`Fehler beim Aufräumen offener Tasks: ${tasksFetchErr.message}. Reset wurde abgebrochen.`);
          return;
        }

        // Filter: beim Reset alles abraeumen AUSSER erfolgreich abgeschlossenen
        // Tasks (completed ohne last_error), die als Audit-Trail erhalten bleiben.
        // Diese Invertierung deckt pending, partially_fulfilled, escalated,
        // fehlgeschlagene completed (mit last_error) UND jeden kuenftigen Status
        // automatisch ab. Zuvor fehlte 'escalated' in der Aufzaehlung, wodurch
        // eskalierte Alt-Tasks Resets ueberlebten und beim Re-Finalisieren als
        // Dublette neben dem frischen Task haengen blieben.
        const activeTasks = (allTasks ?? []).filter(t =>
          !(t.status === 'completed' && !t.last_error)
        );

        if (activeTasks.length > 0) {
          // Staging-Files aus xtax_portal_release-Tasks einsammeln
          const stagingPaths: string[] = [];
          for (const task of activeTasks) {
            if (task.task_type === 'xtax_portal_release' && task.context) {
              const ctx = task.context as Record<string, unknown>;
              const paths = ctx.staging_attachment_paths;
              if (Array.isArray(paths)) {
                stagingPaths.push(...(paths as string[]));
              }
            }
          }

          // Staging-Files löschen (nicht-blockierend: Files dürfen verwaisen,
          // weil der Task-Delete trotzdem stattfinden muss)
          if (stagingPaths.length > 0) {
            const { error: removeErr } = await supabase.storage
              .from('secure-customer-documents')
              .remove(stagingPaths);
            if (removeErr) {
              console.warn('[Reset] Staging-Files-Cleanup fehlgeschlagen (non-blocking):', removeErr.message);
            } else {
              console.log(`[Reset] ${stagingPaths.length} Staging-File(s) entfernt`);
            }
          }

          // Tasks löschen (blockierend: muss klappen, sonst Abbruch)
          const taskIds = activeTasks.map(t => t.id);
          const { error: tasksDeleteErr } = await supabase
            .from('datev_pending_tasks')
            .delete()
            .in('id', taskIds);

          if (tasksDeleteErr) {
            console.error('[Reset] Pending-Tasks-Delete fehlgeschlagen:', tasksDeleteErr);
            alert(`Fehler beim Löschen offener Tasks: ${tasksDeleteErr.message}. Reset wurde abgebrochen.`);
            return;
          }

          console.log(`[Reset] ${activeTasks.length} aktive Pending-Task(s) gelöscht für ${latestStatus.month}/${latestStatus.year}`);
        }
      } catch (cleanupErr) {
        const msg = cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr);
        console.error('[Reset] Pending-Task-Cleanup fehlgeschlagen:', msg);
        alert(`Fehler beim Aufräumen offener Tasks: ${msg}. Reset wurde abgebrochen.`);
        return;
      }
      // === Ende Pending-Task-Cleanup ===

      // Lösche den neuesten Status-Eintrag
      const { error: deleteError } = await supabase
        .from('customer_monthly_status')
        .delete()
        .eq('id', latestStatus.id);

      if (deleteError) {
        console.error('Error deleting status:', deleteError);
        alert('Fehler beim Zurücksetzen des Monats');
        return;
      }

      // Rechnungsentwurf für den zurückgesetzten Monat löschen (nur Status 'entwurf')
      const draftDeleteResult = await invoiceService.deleteDraftByCustomerMonth(
        view.customer.id,
        latestStatus.year,
        latestStatus.month
      );
      if (draftDeleteResult.deleted) {
        console.log(`[Reset] Rechnungsentwurf für ${latestStatus.month}/${latestStatus.year} gelöscht`);
      }

      // Hole die aktualisierten Status-Daten (ohne den gelöschten Eintrag)
      const { data: updatedStatusData } = await supabase
        .from('customer_monthly_status')
        .select('*')
        .eq('customer_id', view.customer.id);

      // Berechne den neuen Arbeitsmonat
      const newWorkingMonth = getCurrentWorkingMonth(view.customer, updatedStatusData || []);
      console.log('Neuer Arbeitsmonat:', newWorkingMonth.year, '/', newWorkingMonth.month);

      // Finde den Status für den neuen Monat (falls vorhanden)
      // WICHTIG (Frist ab Anlieferung): Lookup VOR der Fristberechnung,
      // weil die SSoT-Funktion die Anlieferungsmarkierung aus dem Status braucht.
      const newStatus = (updatedStatusData || []).find(s => 
        s.year === newWorkingMonth.year && 
        s.month === newWorkingMonth.month
      );

      // Hinweis: Der frueher hier gebildete Vormonats-Status entfaellt. Die
      // relativen Spezialfristen ankern am Lohnlauf des ARBEITSMONATS und
      // bekommen deshalb newStatus als Ankerstatus.
      
      // Lade Deadline-Daten für den neuen Monat UND FOLGEMONAT (wichtig für "monatlich_versetzt"!)
      const { data: deadlineData } = await supabase
        .from('monthly_deadlines')
        .select('*')
        .or(`and(year.eq.${newWorkingMonth.year},month.eq.${newWorkingMonth.month}),and(year.eq.${newWorkingMonth.month === 12 ? newWorkingMonth.year + 1 : newWorkingMonth.year},month.eq.${newWorkingMonth.month === 12 ? 1 : newWorkingMonth.month + 1})`);
      
      // Berechne neue Standard-Deadlines
      const newDeadlines: CustomerDeadlineView['deadlines'] = {};
      
      if (view.customer.erster_lohnlauf_tag) {
        newDeadlines.lohnlauf_1 = calculateEffectiveLohnlaufDate(
          view.customer,
          1,
          newWorkingMonth.year,
          newWorkingMonth.month,
          newStatus || null
        );
      }
      
      if (view.customer.zweiter_lohnlauf_tag) {
        newDeadlines.lohnlauf_2 = calculateEffectiveLohnlaufDate(
          view.customer,
          2,
          newWorkingMonth.year,
          newWorkingMonth.month,
          newStatus || null
        );
      }

      // Zwei-Lauf-Modell: eine Kachel je konfiguriertem Lohnlauf (neuer Arbeitsmonat).
      const lsConfigsNeu = lohnscheinConfigsRef.current.get(view.customer.id) || [];
      let newLohnscheinPeriode1: { year: number; month: number } | undefined;
      let newLohnscheinPeriode2: { year: number; month: number } | undefined;
      for (const cfg of lsConfigsNeu) {
        const res = calculateLohnscheinVersandForLauf(view.customer, cfg, newWorkingMonth.year, newWorkingMonth.month, newStatus || null);
        if (cfg.lohnlauf === 1) {
          newDeadlines.lohnschein_versand_1 = res.faelligkeit;
          newLohnscheinPeriode1 = res.periode;
        } else {
          newDeadlines.lohnschein_versand_2 = res.faelligkeit;
          newLohnscheinPeriode2 = res.periode;
        }
      }

      // NACHZÜGLER neu bestimmen: Nach dem Reset ist der neueste Status in aller
      // Regel nicht mehr abgeschlossen, dann ist die Liste leer. Bleibt ein
      // älterer abgeschlossener Monat übrig, wird dessen offener Lauf korrekt
      // wieder angezeigt.
      const resetNachzuegler = await ladeNachzueglerFuerKunden(view.customer, updatedStatusData || []);
      // WARTET AUF KUNDEN: relativ konfigurierte Lohnschein-Läufe (Konfiguration unverändert)
      const resetLohnscheinRelativ = lohnscheinRelativAus(lsConfigsNeu);

      // OPPM QR-Code-Versand: Faelligkeit und Periode fuer den neuen Arbeitsmonat
      let resetOppmQrPeriode: { year: number; month: number } | undefined;
      if (hasOppmQrVersand(view.customer)) {
        const res = calculateOppmQrVersandMitPeriode(
          view.customer,
          newWorkingMonth.year,
          newWorkingMonth.month,
          newStatus || null
        );
        newDeadlines.oppm_qr_versand = res.faelligkeit;
        resetOppmQrPeriode = res.periode;
      }

      // OPPM Monatsuebersicht QR-Code-Versand: analog
      let resetMonatsuebersichtQrPeriode: { year: number; month: number } | undefined;
      if (hasMonatsuebersichtQrVersand(view.customer)) {
        const res = calculateMonatsuebersichtQrVersandMitPeriode(
          view.customer,
          newWorkingMonth.year,
          newWorkingMonth.month,
          newStatus || null
        );
        newDeadlines.monatsuebersicht_qr_versand = res.faelligkeit;
        resetMonatsuebersichtQrPeriode = res.periode;
      }

      // Erledigung, offene Auftraege und Nachzuegler der Spezialfristen neu
      // bestimmen. Nach dem Reset ist der neueste Status in aller Regel nicht
      // mehr abgeschlossen, dann ist die Nachzuegler-Liste leer.
      const resetSpezialfrist = await ladeSpezialfristStandFuerKunden(
        view.customer,
        updatedStatusData || [],
        resetOppmQrPeriode,
        resetMonatsuebersichtQrPeriode
      );
      
      // GEÄNDERT: Verwende neue calculateLohnsteuerDeadline Funktion
      const lohnsteuerDeadline = calculateLohnsteuerDeadline(
        newWorkingMonth.year,
        newWorkingMonth.month,
        view.customer.lohnsteuer_zeitraum,
        deadlineData || []
      );
      if (lohnsteuerDeadline) {
        newDeadlines.lohnsteuer = lohnsteuerDeadline;
      }
      
      // Standard SV-Beiträge Logik bleibt unverändert
      const deadline = deadlineData?.find(d => d.year === newWorkingMonth.year && d.month === newWorkingMonth.month);
      if (deadline?.beitragsnachweis_frist) {
        newDeadlines.sv_beitraege = new Date(deadline.beitragsnachweis_frist);
      }

      // Lade und berechne individuelle Deadlines für den neuen Monat
      const individualDeadlines = view.individualDeadlines || [];
      individualDeadlines.forEach(individualDeadline => {
        const deadlineKey = getIndividualDeadlineKey(individualDeadline.id);
        newDeadlines[deadlineKey] = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
          newWorkingMonth.year,
          newWorkingMonth.month,
          individualDeadline,
          view.customer,
          newStatus || null
        );
      });

      // NACHZÜGLER INDIVIDUELLE FRISTEN neu bestimmen: Nach dem Reset ist der
      // neueste Status in aller Regel nicht mehr abgeschlossen, dann ist die
      // Liste leer. Bleibt ein älterer abgeschlossener Monat übrig, werden
      // dessen noch offene Fristen wieder angezeigt.
      const resetIndividualNachzuegler = baueIndividualNachzueglerListe(
        view.customer,
        updatedStatusData || [],
        individualDeadlines
      );
      
      // Update die komplette View für diesen Kunden. Die Lauf-Fertigmeldungen
      // gehören zum Arbeitsmonat und starten hier unerledigt (Verhalten wie bisher).
      setCustomerDeadlineViews(prevViews => 
        prevViews.map(prevView => {
          if (prevView.customer.id === view.customer.id) {
            const updatedView = {
              ...prevView,
              currentMonth: newWorkingMonth.month,
              currentYear: newWorkingMonth.year,
              deadlines: newDeadlines,
              status: newStatus || null,
              isOverdue: calculateIsOverdue(newDeadlines, newStatus || null, view.customer, individualDeadlines, resetNachzuegler, { lauf1: false, lauf2: false }, { oppmQr: resetSpezialfrist.oppmQrCompleted, monatsuebersichtQr: resetSpezialfrist.monatsuebersichtQrCompleted }, resetSpezialfrist.nachzuegler, resetLohnscheinRelativ, resetIndividualNachzuegler),
              individualDeadlines: individualDeadlines, // WICHTIG: individualDeadlines beibehalten!
              lohnscheinVersandPeriode1: newLohnscheinPeriode1,
              lohnscheinVersandPeriode2: newLohnscheinPeriode2,
              lohnscheinVersandAuftrag1: null,
              lohnscheinVersandAuftrag2: null,
              lohnscheinCompleted1: false,
              lohnscheinCompleted2: false,
              lohnscheinRelativ: resetLohnscheinRelativ,
              nachzuegler: resetNachzuegler,
              oppmQrPeriode: resetOppmQrPeriode,
              monatsuebersichtQrPeriode: resetMonatsuebersichtQrPeriode,
              oppmQrCompleted: resetSpezialfrist.oppmQrCompleted,
              monatsuebersichtQrCompleted: resetSpezialfrist.monatsuebersichtQrCompleted,
              oppmQrVersandTask: resetSpezialfrist.oppmQrVersandTask,
              monatsuebersichtQrVersandTask: resetSpezialfrist.monatsuebersichtQrVersandTask,
              spezialfristNachzuegler: resetSpezialfrist.nachzuegler,
              individualNachzuegler: resetIndividualNachzuegler
            };
            console.log('View aktualisiert:', updatedView);
            return updatedView;
          }
          return prevView;
        })
      );

      console.log(`Kunde ${view.customer.company} wurde auf ${newWorkingMonth.month}/${newWorkingMonth.year} zurückgesetzt`);
    } catch (error) {
      console.error('Error resetting customer month:', error);
      alert('Fehler beim Zurücksetzen des Monats');
    }
  };

  // ============================================
  // SNOOZE FEATURE: Deadline um 1 Tag zurückstellen
  // ============================================
  const snoozeDeadline = async (view: CustomerDeadlineView, field: StatusField) => {
    try {
      // 1. Aktuellen User ermitteln
      const userInfo = await getCurrentUserInfo();
      const snoozedBy = userInfo?.name || 'Unbekannt';
      const snoozedByInitials = userInfo?.initials || '??';

      // 2. Morgen berechnen (immer der nächste Tag, egal zu welcher Uhrzeit)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const snoozedUntil = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`; // YYYY-MM-DD (lokale Zeitzone)

      // 3. Snooze-Info erstellen
      const snoozeInfo: SnoozedDeadlineInfo = {
        snoozed_until: snoozedUntil,
        snoozed_by: snoozedBy,
        snoozed_by_initials: snoozedByInitials,
        snoozed_at: new Date().toISOString()
      };

      // 4. In Datenbank speichern
      if (view.status) {
        // Bestehenden Status aktualisieren
        const updatedSnoozedDeadlines = {
          ...(view.status.snoozed_deadlines || {}),
          [field]: snoozeInfo
        };

        const { error } = await supabase
          .from('customer_monthly_status')
          .update({
            snoozed_deadlines: updatedSnoozedDeadlines,
            updated_at: new Date().toISOString()
          })
          .eq('id', view.status.id);

        if (error) throw error;

        // Lokalen State aktualisieren
        const updatedStatus: CustomerMonthlyStatus = {
          ...view.status,
          snoozed_deadlines: updatedSnoozedDeadlines
        };

        setCustomerDeadlineViews(prevViews =>
          prevViews.map(prevView => {
            if (prevView.customer.id === view.customer.id) {
              return {
                ...prevView,
                status: updatedStatus,
                isOverdue: calculateIsOverdue(
                  prevView.deadlines,
                  updatedStatus,
                  prevView.customer,
                  prevView.individualDeadlines,
                  prevView.nachzuegler,
                  laufFertigmeldungen(prevView),
                  spezialfristFertigmeldungen(prevView),
                  prevView.spezialfristNachzuegler,
                  prevView.lohnscheinRelativ,
                  prevView.individualNachzuegler
                )
              };
            }
            return prevView;
          })
        );
      } else {
        // Neuen Status-Eintrag erstellen (nur mit Snooze-Info)
        const newStatus = {
          customer_id: view.customer.id,
          year: view.currentYear,
          month: view.currentMonth,
          lohnlauf_1_completed: false,
          lohnlauf_2_completed: false,
          lohnsteuer_completed: false,
          sv_beitraege_completed: false,
          fully_completed: false,
          snoozed_deadlines: { [field]: snoozeInfo }
        };

        const { data, error } = await supabase
          .from('customer_monthly_status')
          .insert([newStatus])
          .select();

        if (error) throw error;

        const createdStatus: CustomerMonthlyStatus = data[0];

        setCustomerDeadlineViews(prevViews =>
          prevViews.map(prevView => {
            if (prevView.customer.id === view.customer.id) {
              return {
                ...prevView,
                status: createdStatus,
                isOverdue: calculateIsOverdue(
                  prevView.deadlines,
                  createdStatus,
                  prevView.customer,
                  prevView.individualDeadlines,
                  prevView.nachzuegler,
                  laufFertigmeldungen(prevView),
                  spezialfristFertigmeldungen(prevView),
                  prevView.spezialfristNachzuegler,
                  prevView.lohnscheinRelativ,
                  prevView.individualNachzuegler
                )
              };
            }
            return prevView;
          })
        );
      }

      console.log(`⏸️ Deadline ${field} für ${view.customer.company} zurückgestellt bis ${snoozedUntil} von ${snoozedBy}`);
    } catch (error) {
      console.error('Error snoozing deadline:', error);
      alert('Fehler beim Zurückstellen der Deadline');
    }
  };

  // ============================================
  // FRIST AB ANLIEFERUNG: Datenanlieferung markieren / zurücknehmen
  // ============================================

  // Gemeinsames lokales State-Update für Markieren und Zurücknehmen
  // (Datenanlieferung, Wartet auf Kunden, Kundenrückmeldung): Status ersetzen
  // und die betroffene Lohnlauf-Frist via SSoT neu berechnen (ohne
  // Full-Reload). Fristen, die relativ zum Lohnlauf konfiguriert sind, werden
  // mitberechnet, weil sich ihr Anker verschiebt. isOverdue wird auf den neuen
  // Fristen neu bewertet.
  const applyAnlieferungToLocalState = (
    view: CustomerDeadlineView,
    field: 'lohnlauf_1_completed' | 'lohnlauf_2_completed',
    updatedStatus: CustomerMonthlyStatus
  ) => {
    const lohnlauf: 1 | 2 = field === 'lohnlauf_1_completed' ? 1 : 2;
    const deadlineKey = lohnlauf === 1 ? 'lohnlauf_1' : 'lohnlauf_2';

    setCustomerDeadlineViews(prevViews =>
      prevViews.map(prevView => {
        if (prevView.customer.id === view.customer.id) {
          const updatedDeadlines: CustomerDeadlineView['deadlines'] = {
            ...prevView.deadlines,
            [deadlineKey]: calculateEffectiveLohnlaufDate(
              prevView.customer,
              lohnlauf,
              prevView.currentYear,
              prevView.currentMonth,
              updatedStatus
            )
          };
          // Abhängige Fristen über dieselben SSoT-Funktionen wie in loadData.
          // Fest datierte Fristen ergeben dabei denselben Wert wie zuvor.
          if (hasOppmQrVersand(prevView.customer)) {
            updatedDeadlines.oppm_qr_versand = calculateOppmQrVersandMitPeriode(
              prevView.customer,
              prevView.currentYear,
              prevView.currentMonth,
              updatedStatus
            ).faelligkeit;
          }
          if (hasMonatsuebersichtQrVersand(prevView.customer)) {
            updatedDeadlines.monatsuebersicht_qr_versand = calculateMonatsuebersichtQrVersandMitPeriode(
              prevView.customer,
              prevView.currentYear,
              prevView.currentMonth,
              updatedStatus
            ).faelligkeit;
          }
          for (const cfg of lohnscheinConfigsRef.current.get(prevView.customer.id) || []) {
            const res = calculateLohnscheinVersandForLauf(prevView.customer, cfg, prevView.currentYear, prevView.currentMonth, updatedStatus);
            updatedDeadlines[cfg.lohnlauf === 1 ? 'lohnschein_versand_1' : 'lohnschein_versand_2'] = res.faelligkeit;
          }
          (prevView.individualDeadlines || []).forEach(individualDeadline => {
            if (individualDeadline.is_recurring && individualDeadline.bezug_lohnlauf) {
              updatedDeadlines[getIndividualDeadlineKey(individualDeadline.id)] = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
                prevView.currentYear,
                prevView.currentMonth,
                individualDeadline,
                prevView.customer,
                updatedStatus
              );
            }
          });
          return {
            ...prevView,
            deadlines: updatedDeadlines,
            status: updatedStatus,
            isOverdue: calculateIsOverdue(
              updatedDeadlines,
              updatedStatus,
              prevView.customer,
              prevView.individualDeadlines,
              prevView.nachzuegler,
              laufFertigmeldungen(prevView),
              spezialfristFertigmeldungen(prevView),
              prevView.spezialfristNachzuegler,
              prevView.lohnscheinRelativ,
              prevView.individualNachzuegler
            )
          };
        }
        return prevView;
      })
    );
  };

  // Markiert die Datenanlieferung für einen Lohnlauf (immer mit heutigem Datum).
  // Ab der Markierung berechnet die SSoT-Funktion die Frist als nächsten
  // NRW-Werktag nach dem Anlieferungstag.
  const markDatenAnlieferung = async (view: CustomerDeadlineView, field: StatusField) => {
    // Nur für Lohnlauf-Fristen relevant
    if (field !== 'lohnlauf_1_completed' && field !== 'lohnlauf_2_completed') return;

    try {
      // 1. Aktuellen User ermitteln (gleiche Quelle wie Snooze-Feature)
      const userInfo = await getCurrentUserInfo();
      const markedBy = userInfo?.name || 'Unbekannt';
      const markedByInitials = userInfo?.initials || '??';

      // 2. Anlieferungstag ist immer "heute" (lokale Zeitzone)
      const today = new Date();
      const angeliefertAm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`; // YYYY-MM-DD

      // 3. Anlieferungs-Info erstellen
      const anlieferungInfo: DatenAnlieferungInfo = {
        angeliefert_am: angeliefertAm,
        marked_by: markedBy,
        marked_by_initials: markedByInitials,
        marked_at: new Date().toISOString()
      };

      // 4. In Datenbank speichern
      let updatedStatus: CustomerMonthlyStatus;

      if (view.status) {
        // Bestehenden Status aktualisieren
        const updatedAnlieferungen = {
          ...(view.status.daten_anlieferungen || {}),
          [field]: anlieferungInfo
        };

        const { error } = await supabase
          .from('customer_monthly_status')
          .update({
            daten_anlieferungen: updatedAnlieferungen,
            updated_at: new Date().toISOString()
          })
          .eq('id', view.status.id);

        if (error) throw error;

        updatedStatus = {
          ...view.status,
          daten_anlieferungen: updatedAnlieferungen
        };
      } else {
        // Neuen Status-Eintrag erstellen (nur mit Anlieferungs-Info)
        const newStatus = {
          customer_id: view.customer.id,
          year: view.currentYear,
          month: view.currentMonth,
          lohnlauf_1_completed: false,
          lohnlauf_2_completed: false,
          lohnsteuer_completed: false,
          sv_beitraege_completed: false,
          fully_completed: false,
          daten_anlieferungen: { [field]: anlieferungInfo }
        };

        const { data, error } = await supabase
          .from('customer_monthly_status')
          .insert([newStatus])
          .select();

        if (error) throw error;
        updatedStatus = data[0];
      }

      // 5. Lokalen State aktualisieren (inkl. Neuberechnung der Frist via SSoT)
      applyAnlieferungToLocalState(view, field, updatedStatus);

      console.log(`📥 Datenanlieferung ${field} für ${view.customer.company} markiert (angeliefert am ${angeliefertAm}) von ${markedBy}`);
    } catch (error) {
      console.error('Error marking daten anlieferung:', error);
      alert('Fehler beim Markieren der Datenanlieferung');
    }
  };

  // Nimmt eine Anlieferungs-Markierung zurück (Fehlklick-Korrektur).
  // Danach gilt wieder der fixe Termin als Frist. Entfernt die komplette
  // Markierung, also auch "Wartet auf Kunden" und eine Kundenrückmeldung.
  const unmarkDatenAnlieferung = async (view: CustomerDeadlineView, field: StatusField) => {
    // Nur für Lohnlauf-Fristen relevant
    if (field !== 'lohnlauf_1_completed' && field !== 'lohnlauf_2_completed') return;
    // Ohne bestehende Markierung gibt es nichts zurückzunehmen
    if (!view.status?.daten_anlieferungen?.[field]) return;

    try {
      const updatedAnlieferungen = { ...view.status.daten_anlieferungen };
      delete updatedAnlieferungen[field];

      const { error } = await supabase
        .from('customer_monthly_status')
        .update({
          daten_anlieferungen: updatedAnlieferungen,
          updated_at: new Date().toISOString()
        })
        .eq('id', view.status.id);

      if (error) throw error;

      const updatedStatus: CustomerMonthlyStatus = {
        ...view.status,
        daten_anlieferungen: updatedAnlieferungen
      };

      // Lokalen State aktualisieren (Frist springt via SSoT auf den fixen Termin zurück)
      applyAnlieferungToLocalState(view, field, updatedStatus);

      console.log(`↩️ Datenanlieferung ${field} für ${view.customer.company} zurückgenommen`);
    } catch (error) {
      console.error('Error unmarking daten anlieferung:', error);
      alert('Fehler beim Zurücknehmen der Datenanlieferung');
    }
  };

  // ============================================
  // WARTET AUF KUNDEN: Abrechnung beim Kunden / Kundenrückmeldung
  // ============================================
  // Alles liegt in der bestehenden Anlieferungs-Markierung des Lohnlaufs
  // (daten_anlieferungen). Ablauf: "An Kunden geliefert" lässt die Frist
  // ruhen; die Kundenrückmeldung startet sie neu (nächster NRW-Werktag, siehe
  // calculateEffectiveLohnlaufDate). Mehrere Runden sind möglich.

  // Heutiges Datum als YYYY-MM-DD (lokale Zeitzone, gleiche Konvention wie Snooze)
  const heutigesDatum = (): string => {
    const heute = new Date();
    return `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, '0')}-${String(heute.getDate()).padStart(2, '0')}`;
  };

  // Schreibt eine geänderte Anlieferungs-Markierung zurück und aktualisiert
  // den lokalen State samt abhängiger Fristen.
  const schreibeAnlieferungsMarkierung = async (
    view: CustomerDeadlineView,
    field: 'lohnlauf_1_completed' | 'lohnlauf_2_completed',
    neueMarkierung: DatenAnlieferungInfo
  ) => {
    if (!view.status) return;

    const updatedAnlieferungen = {
      ...(view.status.daten_anlieferungen || {}),
      [field]: neueMarkierung
    };

    const { error } = await supabase
      .from('customer_monthly_status')
      .update({
        daten_anlieferungen: updatedAnlieferungen,
        updated_at: new Date().toISOString()
      })
      .eq('id', view.status.id);

    if (error) throw error;

    const updatedStatus: CustomerMonthlyStatus = {
      ...view.status,
      daten_anlieferungen: updatedAnlieferungen
    };

    applyAnlieferungToLocalState(view, field, updatedStatus);
  };

  // "An Kunden geliefert": Frist ruht bis zur Rückmeldung des Kunden.
  // Setzt eine markierte Datenanlieferung voraus.
  const markWartetAufKunden = async (view: CustomerDeadlineView, field: StatusField) => {
    if (field !== 'lohnlauf_1_completed' && field !== 'lohnlauf_2_completed') return;
    const markierung = view.status?.daten_anlieferungen?.[field];
    if (!markierung) return;

    try {
      const userInfo = await getCurrentUserInfo();
      await schreibeAnlieferungsMarkierung(view, field, {
        ...markierung,
        beim_kunden_seit: heutigesDatum(),
        beim_kunden_by: userInfo?.name || 'Unbekannt',
        beim_kunden_by_initials: userInfo?.initials || '??',
        beim_kunden_at: new Date().toISOString()
      });
      console.log(`📤 ${field} für ${view.customer.company}: an Kunden geliefert, Frist ruht`);
    } catch (error) {
      console.error('Error marking wartet auf kunden:', error);
      alert('Fehler beim Markieren "An Kunden geliefert"');
    }
  };

  // Nimmt "An Kunden geliefert" zurück (Fehlklick-Korrektur). Danach gilt
  // wieder die Frist ab Anlieferung bzw. ab der letzten Rückmeldung.
  const unmarkWartetAufKunden = async (view: CustomerDeadlineView, field: StatusField) => {
    if (field !== 'lohnlauf_1_completed' && field !== 'lohnlauf_2_completed') return;
    const markierung = view.status?.daten_anlieferungen?.[field];
    if (!markierung) return;

    try {
      const neueMarkierung: DatenAnlieferungInfo = { ...markierung };
      delete neueMarkierung.beim_kunden_seit;
      delete neueMarkierung.beim_kunden_by;
      delete neueMarkierung.beim_kunden_by_initials;
      delete neueMarkierung.beim_kunden_at;
      await schreibeAnlieferungsMarkierung(view, field, neueMarkierung);
      console.log(`↩️ ${field} für ${view.customer.company}: "An Kunden geliefert" zurückgenommen`);
    } catch (error) {
      console.error('Error unmarking wartet auf kunden:', error);
      alert('Fehler beim Zurücknehmen von "An Kunden geliefert"');
    }
  };

  // Kundenrückmeldung erhalten: Die Frist läuft neu ab dem heutigen Tag.
  // Nur im Zustand "Wartet auf Kunden" möglich.
  const markKundenRueckmeldung = async (view: CustomerDeadlineView, field: StatusField) => {
    if (field !== 'lohnlauf_1_completed' && field !== 'lohnlauf_2_completed') return;
    const markierung = view.status?.daten_anlieferungen?.[field];
    if (!markierung || !istWartetAufKunden(markierung)) return;

    try {
      const userInfo = await getCurrentUserInfo();
      await schreibeAnlieferungsMarkierung(view, field, {
        ...markierung,
        rueckmeldung_am: heutigesDatum(),
        rueckmeldung_by: userInfo?.name || 'Unbekannt',
        rueckmeldung_by_initials: userInfo?.initials || '??',
        rueckmeldung_at: new Date().toISOString()
      });
      console.log(`📩 ${field} für ${view.customer.company}: Kundenrückmeldung erhalten, Frist läuft neu`);
    } catch (error) {
      console.error('Error marking kunden rueckmeldung:', error);
      alert('Fehler beim Markieren der Kundenrückmeldung');
    }
  };

  // Nimmt eine Kundenrückmeldung zurück (Fehlklick-Korrektur). Die Abrechnung
  // liegt danach wieder beim Kunden.
  const unmarkKundenRueckmeldung = async (view: CustomerDeadlineView, field: StatusField) => {
    if (field !== 'lohnlauf_1_completed' && field !== 'lohnlauf_2_completed') return;
    const markierung = view.status?.daten_anlieferungen?.[field];
    if (!markierung) return;

    try {
      const neueMarkierung: DatenAnlieferungInfo = { ...markierung };
      delete neueMarkierung.rueckmeldung_am;
      delete neueMarkierung.rueckmeldung_by;
      delete neueMarkierung.rueckmeldung_by_initials;
      delete neueMarkierung.rueckmeldung_at;
      await schreibeAnlieferungsMarkierung(view, field, neueMarkierung);
      console.log(`↩️ ${field} für ${view.customer.company}: Kundenrückmeldung zurückgenommen`);
    } catch (error) {
      console.error('Error unmarking kunden rueckmeldung:', error);
      alert('Fehler beim Zurücknehmen der Kundenrückmeldung');
    }
  };

  // Neue Handler für Zendesk-Warnung
  const handleZendeskProceed = async () => {
    if (!zendeskWarningData) return;
    
    const { view, field } = zendeskWarningData;
    
    // Zendesk-Warnung schließen
    setShowZendeskWarning(false);
    setZendeskWarningData(null);
    
    // Mit Finalisierung fortfahren
    await proceedWithFinalization(view, field, true, true);
  };

  const handleZendeskCancel = () => {
    setShowZendeskWarning(false);
    setZendeskWarningData(null);
  };

  return {
    customerDeadlineViews,
    loading,
    loadData,
    toggleStatus,
    loadZendeskForCustomers,
    showUploadModal,
    showReleaseModal,
    currentFinalizationData,
    handleFileUpload,
    handleRelease,
    closeUploadModal,
    closeReleaseModal,
    resetCustomerMonth,
    showZendeskWarning,
    zendeskWarningData,
    handleZendeskProceed,
    handleZendeskCancel,
    // NEUE EXPORTS: Loading States
    isButtonLoading,
    // NEUER EXPORT: Snooze Feature
    snoozeDeadline,
    // NEUE EXPORTS: Frist ab Anlieferung
    markDatenAnlieferung,
    unmarkDatenAnlieferung,
    // NEUE EXPORTS: Wartet auf Kunden
    markWartetAufKunden,
    unmarkWartetAufKunden,
    markKundenRueckmeldung,
    unmarkKundenRueckmeldung,
    // NEUE EXPORTS: Lohnschein-Versand (Stufe 3.2)
    showLohnscheinModal,
    lohnscheinVersandData,
    handleLohnscheinVersandSuccess,
    closeLohnscheinModal,
    // NEUER EXPORT: Nachzügler-Kachel (offener Lohnschein-Lauf aus dem zuletzt
    // abgeschlossenen Monat) — eigener Klickpfad neben toggleStatus
    openLohnscheinNachzuegler,
    // NEUER EXPORT: Nachzuegler-Kachel der periodenbezogenen Spezialfristen
    // (offene QR-Frist aus dem zuletzt abgeschlossenen Monat)
    openSpezialfristNachzuegler,
    // NEUER EXPORT: Nachzügler-Kachel der individuellen Fristen (offene Frist
    // mit offen_nach_finalisierung_ab aus dem zuletzt abgeschlossenen Monat)
    erledigeIndividualNachzuegler,
    // NEUE EXPORTS: OPPM QR-Code-Versand
    showOppmQrModal,
    oppmQrVersandData,
    handleOppmQrRelease,
    handleOppmQrMarkDone,
    closeOppmQrModal,
    // NEUE EXPORTS: OPPM Monatsuebersicht QR-Code-Versand
    showMonatsuebersichtQrModal,
    monatsuebersichtQrVersandData,
    handleMonatsuebersichtQrRelease,
    handleMonatsuebersichtQrMarkDone,
    closeMonatsuebersichtQrModal
  };
};

// FIX (Token-Rotations-Race): Erkennt Auth-/401-Fehler aus Supabase/PostgREST,
// damit das Status-Update nach einem aktiven Session-Refresh einmalig wiederholt
// werden kann. Bewusst eng gefasst auf Auth-Signale; andere Fehler werden NICHT
// als Auth-Fehler behandelt und damit unverändert weitergereicht.
function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { status?: number; code?: string; message?: string };
  if (err.status === 401) return true;
  if (err.code === '401' || err.code === 'PGRST301' || err.code === 'PGRST302') return true;
  const message = (err.message || '').toLowerCase();
  return message.includes('jwt') || message.includes('token') || message.includes('unauthorized');
}
